import { useState, useEffect, useRef, useMemo } from 'react'
import axios from 'axios'
import { hablar, precargarMapaAudios } from '../utils/audioHelper'
import { GrabadorPronunciacion, haySoporteGrabacionPronunciacion, DURACION_MAXIMA_MS } from '../utils/grabadorPronunciacion'

const API = process.env.REACT_APP_API_URL

// ============================================================================
// BANCO DE ACTIVIDADES — variedad entre sesiones
// ----------------------------------------------------------------------------
// Antes, cada leccion mostraba siempre EXACTAMENTE las mismas actividades, en
// el mismo orden, sin importar cuantas veces la repitiera un estudiante. Para
// que se sienta mas dinamico, tratamos "video" y "explicacion" como el marco
// fijo de la leccion (siempre van primero, en su orden original, porque
// presentan el tema), y todo lo demas (match, opcion_multiple, completar,
// produccion_libre, pronunciacion, dictado, ronda_rapida) como un banco de
// practica: si hay mas actividades candidatas de las que se muestran por
// sesion, se elige un subconjunto al azar y se baraja el orden. Desde que
// las lecciones nuevas usan el formato de 10 actividades (1 video +
// 2 explicacion fijas + 7 de practica), subimos el tamaño del banco a 7
// para que las 7 actividades de practica aparezcan siempre completas; si
// una leccion futura tuviera aun mas candidatas de practica, ahi si
// empezaria a variar cuales se muestran cada sesion.
// ============================================================================
const TIPOS_FIJOS = new Set(['video', 'explicacion'])
const TAMANO_BANCO_PRACTICA = 7

// Indica si el navegador permite reconocer la voz (Chrome/Edge si, Firefox no).
// Lo usa la actividad "speaking" para decidir si muestra el boton de grabar
// o un cuadro de texto para escribir la respuesta.
function hayReconocimientoDeVoz() {
  return typeof window !== 'undefined' &&
    !!(window.SpeechRecognition || window.webkitSpeechRecognition)
}

function barajar(arr) {
  const copia = [...arr]
  for (let i = copia.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copia[i], copia[j]] = [copia[j], copia[i]]
  }
  return copia
}

// ── Que fraccion del XP se gana en las actividades de escritura/habla
// libre (produccion_libre, speaking), segun señales basicas de
// esfuerzo: cuanto escribio/hablo mas alla del minimo exigido, que
// tan variado es su vocabulario (no repite la misma palabra), y si
// uso las palabras clave sugeridas de la leccion. OJO: esto NO es una
// evaluacion real de si el ingles esta bien escrito o hablado — eso
// requeriria conectar un servicio de IA que lea el contenido, algo
// que se dejo para mas adelante (cerca del lanzamiento). Mientras
// tanto, esto evita el problema de antes, donde se regalaba el 100%
// del XP con solo escribir 8 palabras cualquiera, sin importar si
// tenian sentido.
// ── Filtro gratis de contenido (heurístico, sin costo) ────────────
// Bloquea respuestas obviamente inválidas ANTES de dejar enviar: texto
// que es solo relleno (la misma letra repetida, tipo "g g g g g g g"),
// texto sin vocales que parece mezcla de teclas al azar, o texto
// escrito en español en una actividad que pide inglés. OJO: esto NO
// verifica si el contenido es correcto ni si tiene sentido real — eso
// (por ejemplo detectar que el estudiante respondió algo con
// información equivocada pero en inglés bien escrito) requiere un
// servicio de IA que lea y entienda el texto, que tiene costo y se
// conectará más adelante (ver "IA real después" en las notas del
// proyecto). Mientras tanto, este filtro gratis sí evita los casos
// más obvios de relleno que antes se aceptaban con solo cumplir el
// conteo mínimo de palabras.
const PALABRAS_ESPANOL = new Set(['el','la','los','las','de','que','y','en','un','una','es','se','no','te','lo','le','da','su','por','son','con','para','al','como','pero','ya','o','este','esta','estos','estas','entre','cuando','muy','sin','sobre','tambien','también','me','hasta','hay','donde','quien','quién','desde','todo','todos','toda','todas','uno','unos','les','ni','contra','otros','otra','otras','otro','ese','esa','esos','esas','eso','ante','ellos','ellas','esto','mi','mí','antes','algunos','algunas','qué','yo','tú','tu','él','ella','nosotros','nosotras','ustedes','vosotros','tanto','mucho','muchos','mucha','muchas','poco','pocos','cual','cuales','nada','algo','porque','sí','si','soy','eres','somos','está','están','tengo','tiene','tienen','gracias','hola','buenos','buenas','dias','días','tardes','noches'])

function pareceEspanol(texto) {
  const limpio = (texto || '').toLowerCase()
  // caracteres que casi nunca aparecen en ingles
  if (/[ñ¿¡]|[áéíóú]/.test(limpio)) return true
  const palabras = limpio.replace(/[^a-záéíóúñ' ]/g, ' ').split(/\s+/).filter(Boolean)
  if (palabras.length === 0) return false
  const enEspanol = palabras.filter(p => PALABRAS_ESPANOL.has(p)).length
  // si mas de un tercio de las palabras son español comunes, asumimos que escribio en español
  return enEspanol / palabras.length > 0.34
}

function pareceBasura(texto) {
  const palabras = (texto || '').trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (palabras.length === 0) return true
  // caso "g g g g g g g g": todas las "palabras" son el mismo token repetido
  const unicas = new Set(palabras)
  if (unicas.size === 1 && palabras.length >= 3) return true
  // palabras sin ninguna vocal (mezcla de teclas al azar, ej. "kjhsdf")
  const soloLetras = palabras.filter(p => /^[a-z']+$/.test(p))
  if (soloLetras.length > 0) {
    const sinVocales = soloLetras.filter(p => p.length >= 3 && !/[aeiou]/.test(p)).length
    if (sinVocales / soloLetras.length > 0.5) return true
  }
  // la misma letra repetida 4+ veces seguidas dentro de una palabra (ej. "kkkkkkk")
  const repetidas = palabras.filter(p => /([a-z])\1{3,}/.test(p)).length
  if (repetidas / palabras.length > 0.5) return true
  return false
}

// Revisa el contenido antes de calcular el XP. Se usa tanto al mostrar
// el botón "Enviar" (para no dejar avanzar con relleno) como dentro de
// calcularFactorEsfuerzo (como respaldo, por si algo se saltó la
// primera revisión).
function validarContenidoIngles(texto) {
  if (pareceBasura(texto)) {
    return { valido: false, motivo: '⚠️ Tu respuesta parece no tener sentido (letras repetidas o al azar). Escribe una respuesta real en inglés.' }
  }
  if (pareceEspanol(texto)) {
    return { valido: false, motivo: '⚠️ Parece que escribiste en español. Esta actividad es en inglés — try again in English.' }
  }
  return { valido: true, motivo: '' }
}

function calcularFactorEsfuerzo(texto, palabrasClave, minimoPalabras) {
  if (!validarContenidoIngles(texto).valido) return 0

  const palabras = (texto || '').trim().toLowerCase().split(/\s+/).filter(Boolean)
  const total = palabras.length
  if (total === 0) return 0

  let puntos = 40 // garantizado por cumplir el minimo exigido para poder enviar

  // Bonus por escribir/hablar mas del minimo (llega al tope al doble del minimo)
  const minimo = Math.max(minimoPalabras || 1, 1)
  const extra = Math.max(0, total - minimo)
  puntos += Math.min(30, Math.round((extra / minimo) * 30))

  // Bonus por variedad de vocabulario (penaliza repetir la misma palabra)
  const unicas = new Set(palabras.map(p => p.replace(/[^a-z']/g, ''))).size
  const variedad = unicas / total
  puntos += Math.min(20, Math.round((variedad / 0.6) * 20))

  // Bonus por usar las palabras clave sugeridas de la leccion (si las hay)
  if (palabrasClave && palabrasClave.length > 0) {
    const textoNormalizado = ' ' + texto.toLowerCase().replace(/[^a-z0-9' ]/g, ' ') + ' '
    const usadas = palabrasClave.filter(pc => textoNormalizado.includes(' ' + pc.toLowerCase() + ' ')).length
    puntos += Math.round((usadas / palabrasClave.length) * 10)
  } else {
    puntos += 10 // la leccion no definio palabras clave: no se penaliza por eso
  }

  return Math.min(100, puntos) / 100
}

function seleccionarActividadesDeSesion(todas) {
  const fijas = todas.filter(a => TIPOS_FIJOS.has(a.tipo)).sort((a, b) => a.orden - b.orden)
  const banco = todas.filter(a => !TIPOS_FIJOS.has(a.tipo))
  const cantidad = Math.min(TAMANO_BANCO_PRACTICA, banco.length)
  const elegidas = barajar(banco).slice(0, cantidad)
  return [...fijas, ...elegidas]
}

// ── Chip de traduccion ────────────────────────────────────
function Traduccion({ texto, style }) {
  const [visible, setVisible] = useState(false)
  if (!texto) return null
  return (
    <span style={{ position:'relative', display:'inline', ...style }}>
      <button onClick={() => setVisible(v => !v)}
        style={{ background:'#FBF3D9', border:'none', borderRadius:4, padding:'1px 6px', fontSize:11, color:'#8A6D1D', cursor:'pointer', marginLeft:4, fontWeight:600 }}>
        {visible ? 'ES ▲' : 'ES ▼'}
      </button>
      {visible && (
        <span style={{ display:'block', background:'#FBF3D9', border:'1px solid #D4AF37', borderRadius:6, padding:'4px 8px', fontSize:12, color:'#374151', marginTop:4, fontStyle:'italic' }}>
          {texto}
        </span>
      )}
    </span>
  )
}

// ── ESCENA ANIMADA ─────────────────────────────────────────
// Dos personajes ilustrados (estilo anime) "conversan": el que habla mueve la
// boca, se ilumina y su voz es masculina o femenina segun el personaje. El
// dialogo se va armando abajo como un chat, con 🔊 para repetir cada frase y
// ES para ver su traduccion.
const COLORES_PERSONAJE = ['#D4AF37', '#3B82F6', '#22C55E', '#8B5CF6', '#EF4444']

// Genero del personaje, para elegir su dibujo y su voz. Se puede indicar en
// los datos de la escena con "genero": "m" | "f" en cualquier linea del
// personaje; si no, se deduce del nombre (Mr./Mrs., o nombres comunes).
const NOMBRES_FEMENINOS = new Set(['laura','maria','mary','anna','ana','sofia','sophie','sophia','emma','olivia','lucy','sarah','sara','julia','kate','katie','emily','jessica','jennifer','lisa','anne','linda','carla','camila','valentina','daniela','paula','andrea','claudia','diana','natalia','karen','susan','rachel','amy','alice','mia','nina','ella','grace','hannah','maya','lily','isabel','lina','sandra','monica','patricia','elena'])
const NOMBRES_MASCULINOS = new Set(['john','mike','michael','david','tom','tim','jack','james','peter','paul','mark','carlos','juan','daniel','andres','luis','pedro','jose','sam','ben','robert','bob','steve','tony','jake','chris','kevin','brian','jorge','mateo','santiago','sebastian','felipe','nicolas','teacher','waiter','doctor','officer'])

function normalizarGenero(g) {
  if (!g) return null
  const s = String(g).toLowerCase()
  if (/^(f|female|mujer|woman|girl)/.test(s)) return 'f'
  if (/^(m|male|hombre|man|boy)/.test(s)) return 'm'
  return null
}

function generoPorNombre(nombre) {
  const n = String(nombre || '').toLowerCase().trim()
  if (/^(mr|mister|sir|se(ñ|n)or|sr)\.?(\s|$)/.test(n)) return 'm'
  if (/^(mrs|ms|miss|madam|se(ñ|n)ora|sra|lady)\.?(\s|$)/.test(n)) return 'f'
  const primero = n.replace(/[^a-záéíóúñ\s]/g, ' ').trim().split(/\s+/)[0]
  if (NOMBRES_FEMENINOS.has(primero)) return 'f'
  if (NOMBRES_MASCULINOS.has(primero)) return 'm'
  if (primero && primero.endsWith('a')) return 'f'
  return null
}

// Colores de piel y pelo para que los personajes se vean distintos
const PERSONAJES_ESTILO = [
  { piel:'#F6D2B5', pelo:'#3B2A22', ojos:'#4A3B8C' },
  { piel:'#E8B896', pelo:'#1F1A2E', ojos:'#2F6B4F' },
  { piel:'#C99572', pelo:'#2B1B14', ojos:'#5B3A1E' },
  { piel:'#F3C9A8', pelo:'#8A4B1F', ojos:'#2B5C8A' },
  { piel:'#9A6B4C', pelo:'#141018', ojos:'#3A2A1A' },
]

// Dibujo del personaje (SVG, sin imagenes externas). Mira de frente y mueve la
// boca cuando "hablando" es true; parpadea de vez en cuando.
function PersonajeAnime({ genero, estilo, ropa, hablando, inclinar }) {
  const { piel, pelo, ojos } = estilo
  const mujer = genero === 'f'
  return (
    <svg viewBox="0 0 120 140" width="112" height="131" aria-hidden="true"
      style={{ display:'block', transform:`rotate(${inclinar}deg)`, transition:'transform .3s ease', overflow:'visible' }}>
      {/* pelo de atras */}
      {mujer
        ? <path d="M22 62 C14 26 40 6 60 6 C80 6 106 26 98 62 C102 92 96 112 90 118 L30 118 C24 112 18 92 22 62 Z" fill={pelo} />
        : <path d="M26 58 C24 24 44 10 60 10 C78 10 98 24 94 58 Z" fill={pelo} />}
      {/* cuerpo / ropa */}
      <path d="M12 140 C14 112 34 104 60 104 C86 104 106 112 108 140 Z" fill={ropa} />
      {mujer
        ? <path d="M46 106 Q60 124 74 106 L70 104 Q60 114 50 104 Z" fill="#fff" opacity=".9" />
        : (<g>
            <path d="M48 105 L60 124 L72 105 Z" fill="#fff" />
            <path d="M57 115 L60 135 L63 115 L60 110 Z" fill="#C0392B" />
          </g>)}
      {/* cuello */}
      <rect x="51" y="92" width="18" height="16" rx="6" fill={piel} />
      <rect x="51" y="92" width="18" height="6" fill="#000" opacity=".08" />
      {/* orejas */}
      <ellipse cx="25" cy="66" rx="5" ry="8" fill={piel} />
      <ellipse cx="95" cy="66" rx="5" ry="8" fill={piel} />
      {/* cara */}
      <path d="M28 56 C28 30 44 20 60 20 C76 20 92 30 92 56 C92 82 78 96 60 96 C42 96 28 82 28 56 Z" fill={piel} />
      {/* flequillo estilo anime */}
      {mujer
        ? <path d="M26 58 C24 28 42 14 60 14 C80 14 98 28 94 58 C90 44 82 34 70 32 C66 40 56 44 44 42 C36 44 30 50 26 58 Z" fill={pelo} />
        : <path d="M26 58 C22 26 44 10 62 12 C82 12 98 28 94 58 C92 46 86 38 78 34 L72 44 L64 32 L54 44 L46 34 C36 38 30 46 26 58 Z" fill={pelo} />}
      {/* mejillas */}
      <ellipse cx="38" cy="74" rx="7" ry="4" fill="#F28B82" opacity=".35" />
      <ellipse cx="82" cy="74" rx="7" ry="4" fill="#F28B82" opacity=".35" />
      {/* cejas */}
      <path d="M39 51 Q46 47 53 50" stroke={pelo} strokeWidth="2.4" fill="none" strokeLinecap="round" />
      <path d="M67 50 Q74 47 81 51" stroke={pelo} strokeWidth="2.4" fill="none" strokeLinecap="round" />
      {/* ojos grandes */}
      <g className="eb-parpadeo">
        <ellipse cx="46" cy="62" rx="7.5" ry="9.5" fill="#fff" />
        <ellipse cx="74" cy="62" rx="7.5" ry="9.5" fill="#fff" />
        <ellipse cx="46" cy="63" rx="5.2" ry="7.6" fill={ojos} />
        <ellipse cx="74" cy="63" rx="5.2" ry="7.6" fill={ojos} />
        <ellipse cx="46" cy="64" rx="2.6" ry="4.2" fill="#10101A" />
        <ellipse cx="74" cy="64" rx="2.6" ry="4.2" fill="#10101A" />
        <circle cx="48.2" cy="59.6" r="2" fill="#fff" />
        <circle cx="76.2" cy="59.6" r="2" fill="#fff" />
        <circle cx="44" cy="66.5" r="1" fill="#fff" opacity=".8" />
        <circle cx="72" cy="66.5" r="1" fill="#fff" opacity=".8" />
      </g>
      {/* pestañas / parpado superior */}
      <path d="M38 55 Q46 50 54 55" stroke="#1A1423" strokeWidth="2.2" fill="none" strokeLinecap="round" />
      <path d="M66 55 Q74 50 82 55" stroke="#1A1423" strokeWidth="2.2" fill="none" strokeLinecap="round" />
      {/* gafas para el personaje masculino */}
      {!mujer && (
        <g stroke="#1B3A8C" strokeWidth="1.8" fill="none" opacity=".9">
          <rect x="35" y="52" width="22" height="20" rx="8" />
          <rect x="63" y="52" width="22" height="20" rx="8" />
          <path d="M57 60 H63" />
        </g>
      )}
      {/* nariz */}
      <path d="M60 70 Q58.5 74 61 75" stroke="#B7835F" strokeWidth="1.4" fill="none" strokeLinecap="round" />
      {/* boca */}
      {hablando ? (
        <g className="eb-boca-habla">
          <ellipse cx="60" cy="84" rx="7" ry="5.5" fill="#7A2434" />
          <ellipse cx="60" cy="86.5" rx="4" ry="2.4" fill="#E56B7F" />
        </g>
      ) : (
        <path d="M52 83 Q60 90 68 83" stroke="#9B3A4A" strokeWidth="2.4" fill="none" strokeLinecap="round" />
      )}
    </svg>
  )
}

// ── FONDOS DE ESCENA ───────────────────────────────────────
// Ilustraciones (SVG, sin imagenes externas) para el escenario donde conversan
// los personajes. Se elige solo segun las palabras de la escena y del dialogo
// ("teacher", "restaurant", "airport"...). Para forzar uno, agrega en la
// actividad "fondo": "aula" | "cafe" | "aeropuerto" | "tienda" | "oficina" |
// "casa" | "parque" | "calle" | "hospital" | "hotel" | "general".
const FONDOS_ESCENA = {
  aula: (
    <g>
      <rect width="800" height="240" fill="#DDE8F6" />
      <rect y="172" width="800" height="68" fill="#C79A64" />
      <rect y="172" width="800" height="6" fill="#A87D4C" />
      {[40, 650].map(x => (
        <g key={x}>
          <rect x={x} y="30" width="110" height="100" rx="4" fill="#9AD1F5" stroke="#fff" strokeWidth="6" />
          <path d={`M${x + 55} 30V130M${x} 80H${x + 110}`} stroke="#fff" strokeWidth="4" />
          <circle cx={x + 82} cy="56" r="13" fill="#FFE27A" />
        </g>
      ))}
      <rect x="250" y="26" width="300" height="122" rx="6" fill="#8B5E3C" />
      <rect x="258" y="34" width="284" height="106" rx="3" fill="#2F6B4F" />
      <text x="400" y="84" textAnchor="middle" fontSize="34" fill="#F4F6F0" opacity=".95" fontFamily="'Segoe Print','Comic Sans MS',cursive">A B C  a b c</text>
      <text x="400" y="122" textAnchor="middle" fontSize="20" fill="#F4F6F0" opacity=".8" fontFamily="'Segoe Print','Comic Sans MS',cursive">Hello! 1 2 3</text>
      <rect x="250" y="146" width="300" height="6" fill="#6B4A2E" />
      <rect x="280" y="143" width="34" height="4" rx="2" fill="#F1ECD8" />
      <circle cx="205" cy="50" r="17" fill="#fff" stroke="#555" strokeWidth="3" />
      <path d="M205 50V39M205 50L213 54" stroke="#333" strokeWidth="2.4" strokeLinecap="round" />
      <rect x="20" y="168" width="130" height="14" rx="3" fill="#A06B3B" />
      <rect x="42" y="182" width="7" height="46" fill="#7A5230" />
      <rect x="122" y="182" width="7" height="46" fill="#7A5230" />
      <rect x="650" y="168" width="130" height="14" rx="3" fill="#A06B3B" />
      <rect x="672" y="182" width="7" height="46" fill="#7A5230" />
      <rect x="752" y="182" width="7" height="46" fill="#7A5230" />
    </g>
  ),
  cafe: (
    <g>
      <rect width="800" height="240" fill="#F3DDBE" />
      <rect y="176" width="800" height="64" fill="#8A5A3A" />
      <path d="M0 0H800V34H0Z" fill="#fff" />
      {Array.from({ length: 16 }).map((_, i) => (
        <path key={i} d={`M${i * 50} 0H${i * 50 + 25}V34Q${i * 50 + 12.5} 46 ${i * 50} 34Z`} fill="#D64541" />
      ))}
      <rect x="40" y="70" width="170" height="86" rx="6" fill="#B9E2F7" stroke="#8A5A3A" strokeWidth="6" />
      <path d="M125 70V156" stroke="#8A5A3A" strokeWidth="4" />
      <rect x="560" y="64" width="190" height="104" rx="6" fill="#2B2B33" stroke="#8A5A3A" strokeWidth="6" />
      <text x="655" y="96" textAnchor="middle" fontSize="20" fontWeight="700" fill="#F7E3A1" fontFamily="'Segoe Print','Comic Sans MS',cursive">MENU</text>
      <path d="M580 112H730M580 130H710M580 148H725" stroke="#F4F4F4" strokeWidth="3" opacity=".7" strokeLinecap="round" />
      <ellipse cx="400" cy="196" rx="70" ry="9" fill="#5E3A22" opacity=".5" />
      <rect x="340" y="176" width="120" height="9" rx="4" fill="#C98B55" />
      <rect x="395" y="185" width="10" height="42" fill="#6B4630" />
      <g><path d="M368 152h26l-3 22h-20z" fill="#fff" stroke="#999" strokeWidth="2" /><path d="M394 158q10 2 0 12" stroke="#999" strokeWidth="2" fill="none" /><path d="M376 144q4-8 0-14M385 144q4-8 0-14" stroke="#fff" strokeWidth="2.5" opacity=".7" fill="none" strokeLinecap="round" /></g>
    </g>
  ),
  aeropuerto: (
    <g>
      <rect width="800" height="240" fill="#E6EEF7" />
      <rect y="176" width="800" height="64" fill="#B9C2CE" />
      <path d="M0 208H800M160 176V240M360 176V240M560 176V240M760 176V240" stroke="#A3ADBA" strokeWidth="2" />
      <rect x="30" y="24" width="460" height="132" fill="#8EC9F2" stroke="#fff" strokeWidth="8" />
      <path d="M260 24V156M30 90H490" stroke="#fff" strokeWidth="5" />
      <ellipse cx="120" cy="60" rx="44" ry="12" fill="#fff" opacity=".85" />
      <g className="eb-avion"><path d="M300 78 l60 -6 l-14 -14 l10 0 l26 14 l30 -2 q10 0 10 6 q0 6 -10 6 l-30 -2 l-26 14 l-10 0 l14 -14 z" fill="#fff" stroke="#9AA7B5" strokeWidth="2" /></g>
      <rect x="540" y="30" width="220" height="86" rx="8" fill="#1D2433" />
      <text x="552" y="54" fontSize="14" fontWeight="700" fill="#FFB84D" fontFamily="monospace">DEPARTURES</text>
      <path d="M552 68H750M552 84H730M552 100H742" stroke="#FFB84D" strokeWidth="4" opacity=".75" strokeLinecap="round" />
      <rect x="590" y="150" width="46" height="58" rx="7" fill="#D64541" /><rect x="602" y="140" width="22" height="12" rx="4" fill="none" stroke="#7A2B28" strokeWidth="4" />
      <rect x="650" y="162" width="40" height="46" rx="6" fill="#3B82F6" /><rect x="660" y="154" width="20" height="10" rx="3" fill="none" stroke="#1E4E9E" strokeWidth="4" />
    </g>
  ),
  tienda: (
    <g>
      <rect width="800" height="240" fill="#FBE9D0" />
      <rect y="178" width="800" height="62" fill="#D8C3A5" />
      <rect x="20" y="40" width="360" height="14" fill="#8A5A3A" />
      <rect x="20" y="104" width="360" height="14" fill="#8A5A3A" />
      <rect x="20" y="168" width="360" height="12" fill="#8A5A3A" />
      {['#EF4444', '#3B82F6', '#22C55E', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#EF4444', '#3B82F6', '#22C55E'].map((c, i) => (
        <rect key={'a' + i} x={30 + i * 35} y={10 + (i % 3) * 4} width="28" height={30 - (i % 3) * 4} rx="3" fill={c} />
      ))}
      {['#F59E0B', '#22C55E', '#EF4444', '#3B82F6', '#EC4899', '#8B5CF6', '#14B8A6', '#F59E0B', '#22C55E', '#EF4444'].map((c, i) => (
        <rect key={'b' + i} x={30 + i * 35} y={72 - (i % 2) * 4} width="28" height={32 + (i % 2) * 4} rx="3" fill={c} />
      ))}
      {['#14B8A6', '#EF4444', '#8B5CF6', '#F59E0B', '#3B82F6', '#EC4899', '#22C55E', '#14B8A6', '#EF4444', '#8B5CF6'].map((c, i) => (
        <rect key={'c' + i} x={30 + i * 35} y={136} width="28" height="32" rx="3" fill={c} />
      ))}
      <rect x="470" y="30" width="200" height="50" rx="10" fill="#D64541" />
      <text x="570" y="64" textAnchor="middle" fontSize="30" fontWeight="800" fill="#fff" fontFamily="Arial, sans-serif">SHOP</text>
      <rect x="560" y="150" width="200" height="28" rx="4" fill="#A06B3B" />
      <rect x="570" y="178" width="8" height="50" fill="#7A5230" /><rect x="742" y="178" width="8" height="50" fill="#7A5230" />
      <rect x="590" y="124" width="50" height="26" rx="3" fill="#2B2B33" />
      <text x="615" y="143" textAnchor="middle" fontSize="14" fontWeight="700" fill="#9AE6B4" fontFamily="monospace">$ 5.00</text>
    </g>
  ),
  oficina: (
    <g>
      <rect width="800" height="240" fill="#E8ECF2" />
      <rect y="178" width="800" height="62" fill="#9AA5B4" />
      <rect x="60" y="26" width="360" height="124" fill="#BFE3FA" stroke="#fff" strokeWidth="8" />
      {[[90, 90, 40], [136, 70, 44], [186, 100, 36], [228, 60, 48], [282, 86, 40], [330, 74, 44], [376, 96, 30]].map(([x, y, w], i) => (
        <rect key={i} x={x} y={y} width={w} height={150 - y} fill="#7C8EA6" opacity=".75" />
      ))}
      <rect x="480" y="40" width="130" height="86" rx="3" fill="#fff" stroke="#9AA5B4" strokeWidth="4" />
      <path d="M496 94l24-20 20 14 26-30 30 22" stroke="#3B82F6" strokeWidth="4" fill="none" strokeLinecap="round" />
      <rect x="640" y="150" width="140" height="14" rx="3" fill="#6B7A90" />
      <rect x="660" y="164" width="8" height="46" fill="#55627A" /><rect x="752" y="164" width="8" height="46" fill="#55627A" />
      <rect x="690" y="118" width="64" height="34" rx="3" fill="#2B2B33" /><rect x="718" y="152" width="8" height="8" fill="#555" />
      <rect x="696" y="124" width="52" height="22" fill="#6CB6FF" opacity=".8" />
      <path d="M30 210h34l-4-34h-26z" fill="#B4673C" /><path d="M47 176q-18-24-30-18M47 176q0-30 6-40M47 176q18-22 30-14" stroke="#2F9E5B" strokeWidth="7" fill="none" strokeLinecap="round" />
    </g>
  ),
  casa: (
    <g>
      <rect width="800" height="240" fill="#F6E3C8" />
      <rect y="178" width="800" height="62" fill="#B98558" />
      <rect x="60" y="34" width="150" height="104" rx="4" fill="#B9E2F7" stroke="#fff" strokeWidth="7" />
      <path d="M135 34V138M60 86H210" stroke="#fff" strokeWidth="4" />
      <path d="M44 28q20 50 0 116M226 28q-20 50 0 116" stroke="#E07A5F" strokeWidth="12" fill="none" strokeLinecap="round" />
      <rect x="280" y="40" width="64" height="50" rx="3" fill="#fff" stroke="#8A5A3A" strokeWidth="5" /><circle cx="312" cy="60" r="9" fill="#F59E0B" /><path d="M290 84l16-14 12 9 14-12" stroke="#2F9E5B" strokeWidth="4" fill="none" />
      <rect x="360" y="52" width="46" height="62" rx="3" fill="#fff" stroke="#8A5A3A" strokeWidth="5" /><circle cx="383" cy="76" r="10" fill="#3B82F6" opacity=".7" />
      <rect x="470" y="104" width="290" height="62" rx="16" fill="#5B8DEF" />
      <rect x="470" y="86" width="290" height="40" rx="16" fill="#6F9CF3" />
      <rect x="486" y="150" width="258" height="30" rx="8" fill="#4A78D6" />
      <rect x="484" y="176" width="10" height="16" fill="#6B4630" /><rect x="736" y="176" width="10" height="16" fill="#6B4630" />
      <rect x="430" y="70" width="6" height="108" fill="#6B4630" /><path d="M412 70h42l-10 -28h-22z" fill="#FFE27A" />
    </g>
  ),
  parque: (
    <g>
      <defs><linearGradient id="ebCieloParque" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#7EC8F2" /><stop offset="1" stopColor="#DDF2FF" /></linearGradient></defs>
      <rect width="800" height="240" fill="url(#ebCieloParque)" />
      <circle cx="680" cy="52" r="26" fill="#FFE27A" />
      <g className="eb-nube"><ellipse cx="160" cy="58" rx="46" ry="14" fill="#fff" opacity=".9" /><ellipse cx="190" cy="48" rx="30" ry="14" fill="#fff" opacity=".9" /></g>
      <g className="eb-nube eb-nube2"><ellipse cx="470" cy="38" rx="40" ry="12" fill="#fff" opacity=".85" /><ellipse cx="496" cy="30" rx="26" ry="12" fill="#fff" opacity=".85" /></g>
      <path d="M0 150Q120 110 260 140T520 130T800 144V240H0Z" fill="#8ED081" />
      <path d="M0 184Q200 160 400 182T800 176V240H0Z" fill="#6DBB63" />
      <path d="M300 240Q360 200 420 182Q480 200 540 240Z" fill="#E7D3A8" />
      {[[90, 120], [700, 110]].map(([x, y], i) => (
        <g key={i}><rect x={x - 6} y={y} width="12" height="60" fill="#7A5230" /><circle cx={x} cy={y - 6} r="34" fill="#3FA34D" /><circle cx={x - 22} cy={y + 8} r="22" fill="#4CB85A" /><circle cx={x + 22} cy={y + 8} r="22" fill="#4CB85A" /></g>
      ))}
      <rect x="560" y="176" width="110" height="10" rx="3" fill="#A06B3B" /><rect x="566" y="186" width="6" height="22" fill="#7A5230" /><rect x="658" y="186" width="6" height="22" fill="#7A5230" /><rect x="560" y="156" width="110" height="8" rx="3" fill="#A06B3B" />
    </g>
  ),
  calle: (
    <g>
      <rect width="800" height="240" fill="#CFE6F7" />
      <rect y="186" width="800" height="54" fill="#5C6270" />
      <path d="M0 214H800" stroke="#F1D45A" strokeWidth="4" strokeDasharray="30 22" />
      <rect y="170" width="800" height="16" fill="#C9CDD4" />
      {[[10, 60, 120, '#E07A5F'], [140, 30, 130, '#F2CC8F'], [280, 76, 110, '#81B29A'], [400, 40, 126, '#8E9AAF'], [536, 70, 116, '#E9B872'], [662, 36, 134, '#B8A1D9']].map(([x, y, w, c], i) => (
        <g key={i}>
          <rect x={x} y={y} width={w} height={170 - y} fill={c} />
          {[0, 1, 2].map(r => [0, 1].map(k => (
            <rect key={r + '-' + k} x={x + 16 + k * (w / 2 - 6)} y={y + 14 + r * 32} width={w / 2 - 28} height="20" rx="2" fill="#FFF6D6" opacity=".9" />
          )))}
        </g>
      ))}
      <rect x="318" y="112" width="6" height="64" fill="#4A4E58" /><rect x="296" y="96" width="50" height="22" rx="4" fill="#2F6BD6" /><text x="321" y="112" textAnchor="middle" fontSize="13" fontWeight="800" fill="#fff" fontFamily="Arial, sans-serif">BUS</text>
    </g>
  ),
  hospital: (
    <g>
      <rect width="800" height="240" fill="#EAF6F6" />
      <rect y="178" width="800" height="62" fill="#C9DDE0" />
      <rect x="350" y="28" width="100" height="100" rx="14" fill="#fff" stroke="#E04B4B" strokeWidth="6" />
      <path d="M390 48h20v22h22v20h-22v22h-20V90h-22V70h22z" fill="#E04B4B" />
      <rect x="60" y="50" width="170" height="80" rx="4" fill="#BFE3FA" stroke="#fff" strokeWidth="6" />
      <path d="M145 50V130" stroke="#fff" strokeWidth="4" />
      <path d="M560 40h190v110H560z" fill="#fff" stroke="#9FB8BC" strokeWidth="4" />
      <path d="M580 70h60M580 92h90M580 114h70" stroke="#9FB8BC" strokeWidth="4" strokeLinecap="round" />
      <circle cx="715" cy="95" r="20" fill="none" stroke="#3FA7A0" strokeWidth="5" /><path d="M715 75v-12" stroke="#3FA7A0" strokeWidth="5" />
      <rect x="600" y="168" width="150" height="14" rx="4" fill="#8FB3B8" />
    </g>
  ),
  hotel: (
    <g>
      <rect width="800" height="240" fill="#F1E3CF" />
      <rect y="180" width="800" height="60" fill="#7B4F3A" />
      <rect x="40" y="30" width="190" height="130" rx="6" fill="#FFF8E7" stroke="#C9A24B" strokeWidth="6" />
      <path d="M56 130h160M56 100h160" stroke="#E5D6B0" strokeWidth="3" />
      <circle cx="135" cy="70" r="26" fill="#FFE27A" opacity=".8" /><path d="M110 70h50" stroke="#C9A24B" strokeWidth="3" />
      <rect x="330" y="30" width="320" height="46" rx="8" fill="#2B2B33" />
      <text x="490" y="62" textAnchor="middle" fontSize="26" fontWeight="800" fill="#F7D67B" fontFamily="Georgia, serif" letterSpacing="4">HOTEL</text>
      <rect x="330" y="140" width="320" height="40" rx="6" fill="#A06B3B" />
      <rect x="330" y="132" width="320" height="12" rx="4" fill="#C98B55" />
      <rect x="690" y="60" width="70" height="100" rx="4" fill="#5A3A28" />
      {[0, 1, 2, 3].map(i => (<g key={i}><circle cx={710 + (i % 2) * 36} cy={86 + Math.floor(i / 2) * 36} r="5" fill="#F7D67B" /><path d={`M${710 + (i % 2) * 36} ${90 + Math.floor(i / 2) * 36}v14`} stroke="#F7D67B" strokeWidth="3" /></g>))}
      <rect x="560" y="124" width="54" height="16" rx="4" fill="#D64541" />
    </g>
  ),
  general: (
    <g>
      <defs><linearGradient id="ebCieloGeneral" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#8FB2F5" /><stop offset="1" stopColor="#E3D3F7" /></linearGradient></defs>
      <rect width="800" height="240" fill="url(#ebCieloGeneral)" />
      <circle cx="120" cy="60" r="60" fill="#fff" opacity=".18" />
      <circle cx="660" cy="90" r="80" fill="#fff" opacity=".16" />
      <circle cx="420" cy="30" r="30" fill="#fff" opacity=".18" />
      <path d="M0 190Q200 160 400 184T800 176V240H0Z" fill="#fff" opacity=".35" />
      <path d="M0 210Q220 190 420 208T800 200V240H0Z" fill="#fff" opacity=".35" />
    </g>
  ),
}

const PALABRAS_FONDO = {
  aula: /\b(class|classroom|teacher|students?|school|academy|lesson|homework|exam|university|college|alphabet|letters?|professor)\b/g,
  cafe: /\b(restaurant|cafe|coffee|menu|waiter|waitress|order|lunch|dinner|breakfast|food|drinks?|bill|tea|sandwich|pizza)\b/g,
  aeropuerto: /\b(airport|flight|plane|passport|luggage|suitcase|boarding|gate|ticket|customs|airline|travel)\b/g,
  tienda: /\b(shop|store|market|buy|price|how much|supermarket|mall|cashier|clothes|size|sale|cost)\b/g,
  oficina: /\b(office|boss|manager|meeting|interview|job|colleague|company|email|desk|coworker|resume)\b/g,
  casa: /\b(home|house|kitchen|family|mom|dad|mother|father|living room|bedroom|brother|sister|apartment)\b/g,
  parque: /\b(park|beach|picnic|garden|walk|weather|sunny|rain|hiking|dog)\b/g,
  calle: /\b(bus|train|station|directions|street|turn|corner|city|subway|taxi|where is|map|bank)\b/g,
  hospital: /\b(doctor|hospital|sick|pharmacy|medicine|nurse|headache|clinic|health)\b/g,
  hotel: /\b(hotel|reception|check-in|check in|reservation|booking|guest|key card)\b/g,
}

// Elige el fondo segun el texto de la escena (pesa doble) y del dialogo
function tipoDeFondo(escena, lineas, forzado) {
  if (forzado && FONDOS_ESCENA[forzado]) return forzado
  const textoEscena = String(escena || '').toLowerCase()
  const textoDialogo = (lineas || []).map(l => l.texto).join(' ').toLowerCase()
  let mejor = 'general', mejorPuntaje = 0
  Object.keys(PALABRAS_FONDO).forEach(tipo => {
    const p = (textoEscena.match(PALABRAS_FONDO[tipo]) || []).length * 2 + (textoDialogo.match(PALABRAS_FONDO[tipo]) || []).length
    if (p > mejorPuntaje) { mejor = tipo; mejorPuntaje = p }
  })
  return mejor
}

function FondoEscena({ tipo }) {
  return (
    <svg viewBox="0 0 800 240" preserveAspectRatio="xMidYMid slice" aria-hidden="true"
      style={{ position:'absolute', inset:0, width:'100%', height:'100%', display:'block' }}>
      {FONDOS_ESCENA[tipo] || FONDOS_ESCENA.general}
    </svg>
  )
}

function EscenaAnimada({ escena, transcripcion, fondo }) {
  const lineas = transcripcion || []
  const tipoFondo = tipoDeFondo(escena, lineas, fondo)
  const todosHablantes = [...new Set(lineas.map(l => l.hablante))]
  const hablantes = todosHablantes.slice(0, 2)
  const [indice, setIndice] = useState(-1)
  const [reproduciendo, setReproduciendo] = useState(false)
  const [traduccionesAbiertas, setTraduccionesAbiertas] = useState({})
  const timeoutRef = useRef(null)
  const chatRef = useRef(null)

  // Genero de cada personaje: el indicado en los datos, o el deducido del
  // nombre; si no hay forma de saberlo, se alternan (primero masculino).
  const generos = {}
  todosHablantes.forEach((nombre, i) => {
    const explicito = normalizarGenero(lineas.find(l => l.hablante === nombre && l.genero)?.genero)
    generos[nombre] = explicito || generoPorNombre(nombre) || (i % 2 === 0 ? 'm' : 'f')
  })

  function iniciar() {
    setTraduccionesAbiertas({})
    setIndice(0)
    setReproduciendo(true)
  }

  useEffect(() => {
    if (!reproduciendo || indice < 0 || indice >= lineas.length) {
      if (indice >= lineas.length) setReproduciendo(false)
      return
    }
    // La escena avanza de una linea a la siguiente solo cuando el navegador
    // termino de decir la linea actual (con una pausa corta para que se note
    // el cambio). Como red de seguridad, si nunca avisa que termino, avanza
    // igual pasado un tiempo calculado segun el largo del texto.
    let yaAvanzo = false
    function avanzar() {
      if (yaAvanzo) return
      yaAvanzo = true
      timeoutRef.current = setTimeout(() => setIndice(i => i + 1), 500)
    }
    hablar(lineas[indice].texto, avanzar, { genero: generos[lineas[indice].hablante] })
    const redSeguridad = setTimeout(avanzar, Math.max(4000, lineas[indice].texto.length * 90))
    return () => { yaAvanzo = true; clearTimeout(timeoutRef.current); clearTimeout(redSeguridad) }
  }, [indice, reproduciendo])

  // Mantiene el chat desplazado hasta la ultima frase (solo el recuadro, no la pagina)
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight
  }, [indice])

  const lineaActual = indice >= 0 && indice < lineas.length ? lineas[indice] : null
  const visibles = indice < 0 ? 0 : Math.min(indice + 1, lineas.length)

  function colorDe(nombre) {
    const i = todosHablantes.indexOf(nombre)
    return COLORES_PERSONAJE[(i >= 0 ? i : 0) % COLORES_PERSONAJE.length]
  }

  function alternarTraduccion(i) {
    setTraduccionesAbiertas(prev => ({ ...prev, [i]: !prev[i] }))
  }

  // Se llama como funcion (no como componente) para que React no lo
  // reconstruya en cada render y la animacion no se reinicie.
  function renderPersonaje(nombre, lado) {
    const idx = todosHablantes.indexOf(nombre)
    const activo = lineaActual?.hablante === nombre
    return (
      <div key={nombre} style={{
        display:'flex', flexDirection:'column', alignItems:'center', gap:0,
        animation: activo ? 'personajeHabla .5s ease infinite' : 'personajeIdle 2.4s ease-in-out infinite',
        opacity: reproduciendo && !activo ? .6 : 1,
        filter: activo ? 'drop-shadow(0 0 14px rgba(255,255,255,0.55)) drop-shadow(0 2px 3px rgba(0,0,0,0.3))' : 'drop-shadow(0 2px 3px rgba(0,0,0,0.3))',
        transition:'opacity .2s ease, filter .2s ease'
      }}>
        <PersonajeAnime
          genero={generos[nombre]}
          estilo={PERSONAJES_ESTILO[(idx >= 0 ? idx : 0) % PERSONAJES_ESTILO.length]}
          ropa={colorDe(nombre)}
          hablando={activo}
          inclinar={lado === 'izq' ? 4 : -4}
        />
        <span style={{ fontSize:12, fontWeight:700, color:'#fff', background:'rgba(13,31,82,0.78)', padding:'2px 12px', borderRadius:999, marginTop:-26, marginBottom:8, position:'relative' }}>{nombre}</span>
      </div>
    )
  }

  return (
    <div style={{ background:'linear-gradient(135deg,#0D1F52,#1B3A8C)', borderRadius:14, padding:28, marginBottom:20, color:'#fff', position:'relative', overflow:'hidden' }}>
      <style>{`
        @keyframes personajeIdle { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
        @keyframes personajeHabla { 0%,100% { transform: scale(1); } 50% { transform: scale(1.04); } }
        @keyframes burbujaAparece { from { opacity:0; transform: translateY(10px) scale(.96); } to { opacity:1; transform: translateY(0) scale(1); } }
        @keyframes ebBocaHabla { 0%,100% { transform: scaleY(.35); } 50% { transform: scaleY(1); } }
        @keyframes ebParpadeo { 0%,92%,100% { transform: scaleY(1); } 96% { transform: scaleY(.1); } }
        .eb-boca-habla { transform-box: fill-box; transform-origin: center; animation: ebBocaHabla .32s ease-in-out infinite; }
        .eb-parpadeo { transform-box: fill-box; transform-origin: center; animation: ebParpadeo 4s ease-in-out infinite; }
        @keyframes ebDeriva { 0% { transform: translateX(-30px); } 100% { transform: translateX(40px); } }
        @keyframes ebAvion { 0% { transform: translateX(-200px); } 100% { transform: translateX(260px); } }
        .eb-nube { animation: ebDeriva 14s ease-in-out infinite alternate; }
        .eb-nube2 { animation-duration: 19s; }
        .eb-avion { animation: ebAvion 12s linear infinite; }
        @media (prefers-reduced-motion: reduce) {
          [style*="personajeIdle"], [style*="personajeHabla"], [style*="burbujaAparece"], .eb-boca-habla, .eb-parpadeo, .eb-nube, .eb-avion { animation: none !important; }
        }
      `}</style>

      {/* Escenario: fondo ilustrado segun el tema + los dos personajes al frente */}
      <div style={{ position:'relative', height:250, borderRadius:12, overflow:'hidden', marginBottom:16, boxShadow:'0 6px 20px rgba(0,0,0,0.25)' }}>
        <FondoEscena tipo={tipoFondo} />
        {hablantes.length >= 2 && (
          <div style={{ position:'absolute', left:0, right:0, bottom:0, display:'flex', justifyContent:'space-evenly', alignItems:'flex-end' }}>
            {renderPersonaje(hablantes[0], 'izq')}
            {renderPersonaje(hablantes[1], 'der')}
          </div>
        )}
      </div>

      <p style={{ opacity:.85, fontSize:14, maxWidth:460, margin:'0 auto', textAlign:'center' }}>{escena}</p>

      {lineas.length > 0 && (
        <div style={{ display:'flex', justifyContent:'center', marginTop:16 }}>
          <button onClick={iniciar} disabled={reproduciendo}
            style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.3)', color:'#fff', borderRadius:10, padding:'8px 20px', fontFamily:'inherit', fontSize:13, fontWeight:700, cursor: reproduciendo ? 'default' : 'pointer' }}>
            {reproduciendo ? '▶ Reproduciendo...' : indice >= lineas.length ? '↻ Ver de nuevo' : '▶ Ver escena'}
          </button>
        </div>
      )}

      {visibles > 0 && (
        <div ref={chatRef} style={{ marginTop:20, maxHeight:300, overflowY:'auto', display:'flex', flexDirection:'column', gap:10, paddingRight:4 }}>
          {lineas.slice(0, visibles).map((l, i) => {
            const derecha = todosHablantes.indexOf(l.hablante) % 2 === 1
            const color = colorDe(l.hablante)
            const sonando = i === indice
            const abierta = !!traduccionesAbiertas[i]
            return (
              <div key={i} style={{
                alignSelf: derecha ? 'flex-end' : 'flex-start', maxWidth:'82%',
                background: derecha ? 'rgba(255,255,255,0.95)' : '#fff', color:'#1A1A2E',
                borderRadius: derecha ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                padding:'8px 12px', borderLeft:'4px solid ' + color,
                boxShadow: sonando ? '0 0 0 2px rgba(255,255,255,0.6)' : '0 2px 8px rgba(0,0,0,0.2)',
                animation:'burbujaAparece .3s ease both'
              }}>
                <div style={{ fontSize:11, fontWeight:700, color, marginBottom:2 }}>{l.hablante}</div>
                <div style={{ fontSize:14, fontWeight:600, lineHeight:1.4 }}>{l.texto}</div>
                {abierta && l.traduccion && (
                  <div style={{ fontSize:12, color:'#6B7280', fontStyle:'italic', marginTop:4 }}>{l.traduccion}</div>
                )}
                <div style={{ display:'flex', gap:6, marginTop:6 }}>
                  <button onClick={() => hablar(l.texto, undefined, { genero: generos[l.hablante] })} disabled={reproduciendo} aria-label="Escuchar frase"
                    style={{ background:'#E8EFFE', border:'none', borderRadius:999, padding:'2px 10px', fontSize:12, cursor: reproduciendo ? 'default' : 'pointer', opacity: reproduciendo ? .5 : 1, fontFamily:'inherit' }}>
                    🔊
                  </button>
                  {l.traduccion && (
                    <button onClick={() => alternarTraduccion(i)}
                      style={{ background:'#FBF3D9', border:'none', borderRadius:999, padding:'2px 10px', fontSize:11, fontWeight:700, color:'#8A6D1D', cursor:'pointer', fontFamily:'inherit' }}>
                      {abierta ? 'ES ▲' : 'ES ▼'}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── ACT 1: VIDEO / ESCENA ─────────────────────────────────
function ActividadVideo({ actividad, onCompletar }) {
  const [visto, setVisto] = useState(false)
  const c = actividad.contenido || {}
  return (
    <div>
      <EscenaAnimada escena={c.escena} transcripcion={c.transcripcion} fondo={c.fondo} />
      {c.vocabulario && (
        <div style={{ marginBottom:20 }}>
          <h3 style={{ fontSize:13, fontWeight:700, color:'#6B7280', marginBottom:10, textTransform:'uppercase' }}>Key vocabulary</h3>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))', gap:8 }}>
            {c.vocabulario.map((v,i) => (
              <div key={i} style={{ background:'#E8EFFE', borderRadius:10, padding:'10px 12px' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{ fontWeight:700, fontSize:13, color:'#1B3A8C' }}>{v.palabra}</span>
                  <button onClick={() => hablar(v.palabra)} style={{ background:'#1B3A8C', border:'none', borderRadius:'50%', width:24, height:24, cursor:'pointer', fontSize:11 }}>🔊</button>
                </div>
                <div style={{ fontSize:12, color:'#6B7280', marginTop:2 }}>{v.traduccion}</div>
                <div style={{ fontSize:11, color:'#8A6D1D', fontStyle:'italic', marginTop:1 }}>{v.fonetica}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {c.transcripcion && (
        <details style={{ marginBottom:20 }}>
          <summary style={{ cursor:'pointer', fontSize:13, fontWeight:600, color:'#1B3A8C', padding:'8px 0' }}>Ver dialogo completo</summary>
          <div style={{ marginTop:10, display:'flex', flexDirection:'column', gap:6 }}>
            {c.transcripcion.map((l,i) => (
              <div key={i} style={{ background:'#F4F6FA', borderRadius:8, padding:'8px 12px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:2 }}>
                  <span style={{ fontWeight:700, fontSize:12, color:'#1B3A8C', minWidth:60 }}>{l.hablante}</span>
                  <span style={{ fontSize:13 }}>{l.texto}</span>
                  <button onClick={() => hablar(l.texto, undefined, { genero: normalizarGenero(l.genero) || generoPorNombre(l.hablante) })} style={{ marginLeft:'auto', background:'#E8EFFE', border:'none', borderRadius:'50%', width:24, height:24, cursor:'pointer', fontSize:11, flexShrink:0 }}>🔊</button>
                </div>
                {l.traduccion && <div style={{ fontSize:11, color:'#9CA3AF', marginLeft:68, fontStyle:'italic' }}>{l.traduccion}</div>}
              </div>
            ))}
          </div>
        </details>
      )}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingTop:16, borderTop:'1px solid #E2E6F0' }}>
        <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer' }}>
          <input type="checkbox" checked={visto} onChange={e => setVisto(e.target.checked)} />
          He revisado el vocabulario y el dialogo
        </label>
        <button disabled={!visto} onClick={() => onCompletar({ xp_ganado: actividad.xp })}
          style={{ background: visto ? '#1B3A8C' : '#E2E6F0', color: visto ? '#fff' : '#6B7280', border:'none', borderRadius:10, padding:'9px 20px', fontFamily:'inherit', fontSize:14, fontWeight:600, cursor: visto ? 'pointer' : 'not-allowed' }}>
          Continuar →
        </button>
      </div>
    </div>
  )
}

// ── ACT 2: EXPLICACION ────────────────────────────────────
function ActividadExplicacion({ actividad, onCompletar }) {
  const bloques = actividad.contenido?.bloques || []
  return (
    <div>
      <div style={{ display:'flex', flexDirection:'column', gap:20, marginBottom:24 }}>
        {bloques.map((b,i) => (
          <div key={i} style={{ background:'#F4F6FA', borderRadius:12, padding:18 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
              <h3 style={{ fontSize:15, fontWeight:700, color:'#1B3A8C' }}>{b.subtitulo}</h3>
              {b.subtitulo_traduccion && <span style={{ fontSize:12, color:'#9CA3AF', fontStyle:'italic' }}>({b.subtitulo_traduccion})</span>}
            </div>
            <p style={{ fontSize:14, color:'#374151', marginBottom:b.ejemplos ? 12 : 0, lineHeight:1.7 }}>{b.texto}</p>
            {b.texto_traduccion && (
              <details style={{ marginBottom: b.ejemplos ? 12 : 0 }}>
                <summary style={{ cursor:'pointer', fontSize:12, color:'#8A6D1D', fontWeight:600 }}>Ver traduccion al español</summary>
                <p style={{ fontSize:12, color:'#6B7280', fontStyle:'italic', marginTop:6, padding:'6px 10px', background:'#FBF3D9', borderRadius:6 }}>{b.texto_traduccion}</p>
              </details>
            )}
            {b.ejemplos && (
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {b.ejemplos.map((ej,j) => (
                  <div key={j} style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:8, padding:'8px 12px' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <span style={{ fontSize:13, fontWeight:500 }}>💬 {ej.texto}</span>
                      <button onClick={() => hablar(ej.texto)} style={{ background:'#E8EFFE', border:'none', borderRadius:'50%', width:26, height:26, cursor:'pointer', fontSize:12, flexShrink:0, marginLeft:8 }}>🔊</button>
                    </div>
                    {ej.traduccion && <div style={{ fontSize:11, color:'#9CA3AF', fontStyle:'italic', marginTop:3 }}>→ {ej.traduccion}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ display:'flex', justifyContent:'flex-end', paddingTop:16, borderTop:'1px solid #E2E6F0' }}>
        <button onClick={() => onCompletar({ xp_ganado: 0 })}
          style={{ background:'#1B3A8C', color:'#fff', border:'none', borderRadius:10, padding:'9px 20px', fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
          Entendido →
        </button>
      </div>
    </div>
  )
}

// ── ACT 3: UNIR COLUMNAS (MATCH) ──────────────────────────
function ActividadMatch({ actividad, onCompletar }) {
  const paresBase = actividad.contenido?.pares || []
  // Cada par necesita una CLAVE UNICA POR INSTANCIA (no por texto), porque
  // varias tarjetas pueden mostrar el mismo texto (ej. "is" para He y She,
  // "are" para You/We/They en el verbo "to be"). Si el estado se rastreara
  // por texto, marcar una tarjeta "is" como resuelta marcaria TODAS las
  // tarjetas "is" como resueltas, dejando la actividad imposible de
  // completar. Usamos p.id si viene en el contenido, o el indice como
  // respaldo.
  const pares = useMemo(
    () => paresBase.map((p, i) => ({ ...p, _key: p.id != null ? String(p.id) : `idx-${i}` })),
    [paresBase]
  )
  const [selIzq, setSelIzq] = useState(null) // _key de la tarjeta izquierda seleccionada
  const [selDer, setSelDer] = useState(null) // _key de la tarjeta derecha seleccionada (instancia exacta)
  const [correctosIzq, setCorrectosIzq] = useState([]) // _keys resueltas del lado izquierdo
  const [correctosDer, setCorrectosDer] = useState([]) // _keys resueltas del lado derecho (instancia exacta)
  const [incorrectos, setIncorrectos] = useState([])   // [_key izq, _key der] en rojo momentaneo
  const [completado, setComp] = useState(false)
  const derechasRef = useRef([...pares].sort(() => Math.random() - 0.5))

  useEffect(() => {
    if (selIzq && selDer) {
      const parIzq = pares.find(p => p._key === selIzq)
      const parDer = derechasRef.current.find(p => p._key === selDer)
      if (parIzq && parDer && parIzq.derecha === parDer.derecha) {
        const nuevosIzq = [...correctosIzq, selIzq]
        const nuevosDer = [...correctosDer, selDer]
        setCorrectosIzq(nuevosIzq)
        setCorrectosDer(nuevosDer)
        if (nuevosIzq.length === pares.length) setTimeout(() => setComp(true), 500)
      } else {
        setIncorrectos([selIzq, selDer])
        setTimeout(() => setIncorrectos([]), 800)
      }
      setSelIzq(null); setSelDer(null)
    }
  }, [selIzq, selDer])

  function colorBtn(key, lado) {
    const parCorrecto = lado === 'izq' ? correctosIzq.includes(key) : correctosDer.includes(key)
    if (parCorrecto) return { bg:'#F0FDF4', border:'#22C55E', color:'#166534' }
    const esIncorrecto = incorrectos.includes(key)
    if (esIncorrecto) return { bg:'#FEF2F2', border:'#EF4444', color:'#991B1B' }
    const seleccionado = lado === 'izq' ? selIzq === key : selDer === key
    if (seleccionado) return { bg:'#E8EFFE', border:'#1B3A8C', color:'#1B3A8C' }
    return { bg:'#fff', border:'#E2E6F0', color:'#1A1A2E' }
  }

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:24 }}>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {pares.map(p => {
            const c = colorBtn(p._key, 'izq')
            const yaCorr = correctosIzq.includes(p._key)
            return (
              <button key={p._key} onClick={() => !yaCorr && setSelIzq(p._key)}
                style={{ padding:'10px 14px', border:`2px solid ${c.border}`, borderRadius:10, background:c.bg, color:c.color, fontFamily:'inherit', fontSize:13, fontWeight:600, cursor: yaCorr ? 'default' : 'pointer', textAlign:'left', transition:'all .15s', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                {p.izquierda}
                {yaCorr && <span>✓</span>}
                {!yaCorr && <button onClick={e => { e.stopPropagation(); hablar(p.izquierda) }} style={{ background:'transparent', border:'none', cursor:'pointer', fontSize:13 }}>🔊</button>}
              </button>
            )
          })}
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {derechasRef.current.map(p => {
            const c = colorBtn(p._key, 'der')
            const yaCorr = correctosDer.includes(p._key)
            return (
              <button key={p._key} onClick={() => !yaCorr && setSelDer(p._key)}
                style={{ padding:'10px 14px', border:`2px solid ${c.border}`, borderRadius:10, background:c.bg, color:c.color, fontFamily:'inherit', fontSize:13, cursor: yaCorr ? 'default' : 'pointer', textAlign:'left', transition:'all .15s', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                {p.derecha}
                {yaCorr && <span>✓</span>}
              </button>
            )
          })}
        </div>
      </div>
      {completado ? (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingTop:16, borderTop:'1px solid #E2E6F0' }}>
          <span style={{ fontSize:15, fontWeight:700, color:'#22C55E' }}>✅ Perfect! +{actividad.xp} XP</span>
          <button onClick={() => onCompletar({ xp_ganado: actividad.xp })}
            style={{ background:'#D4AF37', color:'#0D1F52', border:'none', borderRadius:10, padding:'9px 20px', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor:'pointer' }}>
            Siguiente →
          </button>
        </div>
      ) : (
        <div style={{ fontSize:13, color:'#6B7280', textAlign:'center' }}>
          {correctosIzq.length} / {pares.length} pares correctos
        </div>
      )}
    </div>
  )
}

// ── ACT 4: OPCION MULTIPLE ────────────────────────────────
function ActividadOpcionMultiple({ actividad, onCompletar }) {
  // Normaliza 'opciones': algunas actividades guardan un array de strings
  // (con 'correcta' como indice numerico), otras un array de objetos
  // {id, texto, traduccion} (con 'correcta' igual al id). Convertimos las
  // strings a objetos {id: indice, texto} para que el resto del componente
  // funcione igual sin importar el formato guardado en la base de datos.
  const preguntas = (actividad.contenido?.preguntas || []).map(p => ({
    ...p,
    opciones: (p.opciones || []).map((op, oi) => (typeof op === 'string' ? { id: oi, texto: op } : op))
  }))
  const [respuestas, setResp] = useState({})
  const [validado, setVal] = useState(false)
  const [xpGanado, setXp] = useState(0)

  function validar() {
    if (Object.keys(respuestas).length < preguntas.length) return
    let xp = 0
    preguntas.forEach(p => { if (respuestas[p.id] === p.correcta) xp += actividad.xp / preguntas.length })
    setXp(Math.round(xp)); setVal(true)
  }

  return (
    <div>
      <div style={{ display:'flex', flexDirection:'column', gap:24, marginBottom:24 }}>
        {preguntas.map((p,i) => (
          <div key={p.id}>
            <div style={{ marginBottom:10 }}>
              {p.situacion && (
                <div style={{ background:'#FBF3D9', border:'1px solid #D4AF37', borderRadius:8, padding:'6px 12px', marginBottom:8, fontSize:13 }}>
                  <span style={{ fontStyle:'italic' }}>{p.situacion}</span>
                  {p.situacion_traduccion && <div style={{ fontSize:11, color:'#9CA3AF', marginTop:2 }}>→ {p.situacion_traduccion}</div>}
                </div>
              )}
              <p style={{ fontSize:14, fontWeight:600 }}>
                <strong style={{ color:'#6B7280' }}>{i+1}.</strong> {p.pregunta}
                {p.pregunta_traduccion && <span style={{ display:'block', fontSize:12, color:'#9CA3AF', fontStyle:'italic', marginTop:2 }}>({p.pregunta_traduccion})</span>}
              </p>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {p.opciones.map(op => {
                let bg='#fff', border='#E2E6F0', color='#1A1A2E'
                if (respuestas[p.id]===op.id && !validado) { bg='#E8EFFE'; border='#1B3A8C' }
                if (validado && op.id===p.correcta) { bg='#F0FDF4'; border='#22C55E' }
                if (validado && respuestas[p.id]===op.id && op.id!==p.correcta) { bg='#FEF2F2'; border='#EF4444' }
                return (
                  <button key={op.id} onClick={() => !validado && setResp(r => ({...r, [p.id]: op.id}))}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', border:`1.5px solid ${border}`, borderRadius:10, background:bg, color, fontFamily:'inherit', fontSize:13, cursor: validado ? 'default' : 'pointer', textAlign:'left', width:'100%' }}>
                    <span style={{ width:22, height:22, borderRadius:'50%', background:'#E2E6F0', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:800, flexShrink:0 }}>{typeof op.id === 'number' ? String.fromCharCode(65 + op.id) : String(op.id).toUpperCase()}</span>
                    <div style={{ flex:1 }}>
                      <div>{op.texto}</div>
                      {op.traduccion && <div style={{ fontSize:11, color:'#9CA3AF', fontStyle:'italic' }}>{op.traduccion}</div>}
                    </div>
                    <button onClick={e => { e.stopPropagation(); hablar(op.texto) }} style={{ background:'transparent', border:'none', cursor:'pointer', fontSize:14, flexShrink:0 }}>🔊</button>
                  </button>
                )
              })}
            </div>
            {validado && (
              <div style={{ marginTop:8, padding:'8px 12px', background: respuestas[p.id]===p.correcta ? '#F0FDF4' : '#FEF2F2', borderLeft:`3px solid ${respuestas[p.id]===p.correcta ? '#22C55E' : '#EF4444'}`, borderRadius:'0 8px 8px 0', fontSize:13 }}>
                {respuestas[p.id]===p.correcta ? p.feedback_correcto : p.feedback_incorrecto}
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingTop:16, borderTop:'1px solid #E2E6F0' }}>
        {!validado
          ? <button disabled={Object.keys(respuestas).length < preguntas.length} onClick={validar}
              style={{ background:'#1B3A8C', color:'#fff', border:'none', borderRadius:10, padding:'9px 20px', fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              Comprobar respuestas
            </button>
          : <>
              <span style={{ fontSize:15, fontWeight:700, color:'#22C55E' }}>✅ +{xpGanado} XP</span>
              <button onClick={() => onCompletar({ xp_ganado: xpGanado })}
                style={{ background:'#D4AF37', color:'#0D1F52', border:'none', borderRadius:10, padding:'9px 20px', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor:'pointer' }}>
                Siguiente →
              </button>
            </>
        }
      </div>
    </div>
  )
}

// ── ACT 5: ESCRITURA LIBRE ────────────────────────────────
function ActividadProduccionLibre({ actividad, onCompletar }) {
  const c = actividad.contenido || {}
  const [texto, setTexto] = useState('')
  const [enviado, setEnviado] = useState(false)
  const palabras = texto.trim().split(/\s+/).filter(Boolean)
  const suficiente = palabras.length >= 8
  const validacion = validarContenidoIngles(texto)
  const puedeEnviar = suficiente && validacion.valido
  const factorEsfuerzo = calcularFactorEsfuerzo(texto, c.palabras_clave, 8)
  const xpGanado = Math.round((actividad.xp || 0) * factorEsfuerzo)

  return (
    <div>
      <div style={{ background:'#F4F6FA', borderRadius:12, padding:16, marginBottom:16 }}>
        <p style={{ fontSize:14, fontWeight:600, color:'#1B3A8C', marginBottom:4 }}>{c.prompt}</p>
        {c.prompt_traduccion && <p style={{ fontSize:12, color:'#9CA3AF', fontStyle:'italic' }}>{c.prompt_traduccion}</p>}
      </div>
      {c.palabras_clave && (
        <div style={{ marginBottom:14 }}>
          <p style={{ fontSize:12, fontWeight:600, color:'#6B7280', marginBottom:6 }}>SUGGESTED WORDS:</p>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {c.palabras_clave.map((p,i) => (
              <button key={i} onClick={() => hablar(p)}
                style={{ background:'#E8EFFE', border:'1px solid #1B3A8C', borderRadius:999, padding:'3px 10px', fontSize:12, color:'#1B3A8C', cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
                {p} 🔊
                {c.palabras_clave_traduccion && <span style={{ color:'#9CA3AF', fontSize:11 }}>({c.palabras_clave_traduccion[i]})</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      {!enviado ? (
        <>
          <textarea value={texto} onChange={e => setTexto(e.target.value)}
            placeholder="Write here in English..."
            style={{ width:'100%', minHeight:120, padding:'12px 14px', border:'1.5px solid #E2E6F0', borderRadius:10, fontFamily:'inherit', fontSize:14, outline:'none', resize:'vertical', lineHeight:1.6 }}
          />
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:8 }}>
            <span style={{ fontSize:12, color: suficiente ? '#22C55E' : '#6B7280' }}>
              {palabras.length} palabras {suficiente ? '✓ Listo' : `· escribe al menos ${8-palabras.length} mas`}
            </span>
            <button disabled={!puedeEnviar} onClick={() => setEnviado(true)}
              style={{ background: puedeEnviar ? '#1B3A8C' : '#E2E6F0', color: puedeEnviar ? '#fff' : '#6B7280', border:'none', borderRadius:10, padding:'9px 20px', fontFamily:'inherit', fontSize:14, fontWeight:600, cursor: puedeEnviar ? 'pointer' : 'not-allowed' }}>
              Enviar escrito
            </button>
          </div>
          {suficiente && !validacion.valido && (
            <p style={{ fontSize:12, color:'#EF4444', marginTop:6 }}>{validacion.motivo}</p>
          )}
        </>
      ) : (
        <div>
          <div style={{ background:'#F0FDF4', border:'1px solid #22C55E', borderRadius:10, padding:16, marginBottom:16 }}>
            <p style={{ fontSize:13, fontWeight:600, color:'#166534', marginBottom:8 }}>✅ Tu respuesta:</p>
            <p style={{ fontSize:14, lineHeight:1.7 }}>{texto}</p>
          </div>
          {c.ejemplo_respuesta && (
            <details>
              <summary style={{ cursor:'pointer', fontSize:13, fontWeight:600, color:'#1B3A8C', padding:'6px 0' }}>Ver ejemplo de respuesta</summary>
              <div style={{ marginTop:8, background:'#E8EFFE', borderRadius:8, padding:12 }}>
                <p style={{ fontSize:13, lineHeight:1.7 }}>{c.ejemplo_respuesta}</p>
                {c.ejemplo_traduccion && <p style={{ fontSize:12, color:'#9CA3AF', fontStyle:'italic', marginTop:4 }}>{c.ejemplo_traduccion}</p>}
                <button onClick={() => hablar(c.ejemplo_respuesta)} style={{ marginTop:8, background:'#1B3A8C', border:'none', borderRadius:8, padding:'6px 12px', color:'#fff', fontSize:12, cursor:'pointer' }}>🔊 Escuchar ejemplo</button>
              </div>
            </details>
          )}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:16, paddingTop:16, borderTop:'1px solid #E2E6F0' }}>
            <span style={{ fontSize:14, fontWeight:700, color:'#22C55E' }}>✅ +{xpGanado} XP</span>
            <button onClick={() => onCompletar({ xp_ganado: xpGanado })}
              style={{ background:'#D4AF37', color:'#0D1F52', border:'none', borderRadius:10, padding:'9px 20px', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor:'pointer' }}>
              Continuar →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── ACT 6: COMPLETAR FRASES ───────────────────────────────
function ActividadCompletarFrases({ actividad, onCompletar }) {
  const ejercicios = actividad.contenido?.ejercicios || []
  const banco = actividad.contenido?.banco_palabras || []
  const bancotrad = actividad.contenido?.banco_traduccion || []
  const [resp, setResp] = useState({})
  const [validado, setVal] = useState(false)
  const [correctas, setCor] = useState(0)

  function validar() {
    let ok = 0
    ejercicios.forEach(ej => {
      ej.blancos.forEach(b => {
        if ((resp[`${ej.id}-${b.pos}`]||'').trim().toLowerCase() === b.correcta.toLowerCase()) ok++
      })
    })
    setCor(ok); setVal(true)
  }

  const total = ejercicios.reduce((s,e) => s + e.blancos.length, 0)
  const xpGanado = Math.round((correctas/Math.max(total,1)) * actividad.xp)

  return (
    <div>
      {banco.length > 0 && (
        <div style={{ background:'#F4F6FA', borderRadius:10, padding:14, marginBottom:20 }}>
          <p style={{ fontSize:12, fontWeight:600, color:'#6B7280', marginBottom:8 }}>WORD BANK:</p>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {banco.map((p,i) => (
              <button key={i} onClick={() => hablar(p)}
                style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:6, padding:'4px 10px', fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
                {p} 🔊
                {bancotrad[i] && <span style={{ fontSize:11, color:'#9CA3AF' }}>({bancotrad[i]})</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      <div style={{ display:'flex', flexDirection:'column', gap:14, marginBottom:24 }}>
        {ejercicios.map((ej,i) => {
          const partes = ej.texto.split('___')
          return (
            <div key={ej.id}>
              <div style={{ fontSize:14, lineHeight:2 }}>
                <span style={{ fontWeight:700, color:'#6B7280', marginRight:8 }}>{i+1}.</span>
                {partes.map((parte,j) => (
                  <span key={j}>
                    {parte}
                    {j < ej.blancos.length && (
                      <input type="text" value={resp[`${ej.id}-${j}`]||''}
                        onChange={e => !validado && setResp(r => ({...r, [`${ej.id}-${j}`]: e.target.value}))}
                        placeholder={ej.blancos[j]?.pista || '...'}
                        style={{ border:'none', borderBottom:`2px solid ${validado ? ((resp[`${ej.id}-${j}`]||'').trim().toLowerCase()===ej.blancos[j]?.correcta.toLowerCase() ? '#22C55E' : '#EF4444') : '#1B3A8C'}`, padding:'2px 6px', fontFamily:'inherit', fontSize:14, fontWeight:600, color: validado ? ((resp[`${ej.id}-${j}`]||'').trim().toLowerCase()===ej.blancos[j]?.correcta.toLowerCase() ? '#22C55E' : '#EF4444') : '#1B3A8C', background:'transparent', outline:'none', margin:'0 4px', width: Math.max(80, (ej.blancos[j]?.correcta||'').length*10+20)+'px' }}
                      />
                    )}
                  </span>
                ))}
                {validado && <button onClick={() => hablar(ej.texto.replace('___', ej.blancos[0]?.correcta||''))} style={{ marginLeft:8, background:'#E8EFFE', border:'none', borderRadius:'50%', width:24, height:24, cursor:'pointer', fontSize:11 }}>🔊</button>}
              </div>
              {ej.traduccion && <div style={{ fontSize:11, color:'#9CA3AF', fontStyle:'italic', marginLeft:28, marginTop:2 }}>→ {ej.traduccion.replace('___', `[${ej.blancos[0]?.pista||'...'}]`)}</div>}
            </div>
          )
        })}
      </div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingTop:16, borderTop:'1px solid #E2E6F0' }}>
        {!validado
          ? <button onClick={validar} style={{ background:'#1B3A8C', color:'#fff', border:'none', borderRadius:10, padding:'9px 20px', fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>Comprobar</button>
          : <>
              <span style={{ fontSize:14, fontWeight:700, color:'#22C55E' }}>✅ {correctas}/{total} correctas · +{xpGanado} XP</span>
              <button onClick={() => onCompletar({ xp_ganado: xpGanado })}
                style={{ background:'#D4AF37', color:'#0D1F52', border:'none', borderRadius:10, padding:'9px 20px', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor:'pointer' }}>
                Siguiente →
              </button>
            </>
        }
      </div>
    </div>
  )
}

// ── ACT 7: PRONUNCIACION ─────────────────────────────────
// ANTES el "puntaje" se calculaba comparando de forma muy tosca el texto
// que el reconocimiento de voz del navegador (Web Speech API) creia haber
// escuchado contra la palabra objetivo -- por eso parecia un numero
// practicamente aleatorio: no media la pronunciacion, solo si el motor de
// voz del navegador adivino mas o menos las mismas letras. Ahora se graba
// el audio real y se envia al backend, que lo evalua con Azure AI Speech
// (Pronunciation Assessment) y devuelve un puntaje real de pronunciacion.
//
// Si el navegador no puede grabar audio (falta getUserMedia/AudioContext,
// el estudiante niega el permiso del microfono, o el servicio de Azure no
// esta disponible por cualquier motivo), se cae de vuelta a la alternativa
// auto-reportada ("ya la practique") en vez de bloquear la leccion -- igual
// que antes, nunca se deja trabado al estudiante.
function ActividadPronunciacion({ actividad, onCompletar }) {
  const palabras = actividad.contenido?.palabras || []
  const [idx, setIdx] = useState(0)
  const [escuchando, setEsc] = useState(false)
  const [evaluando, setEvaluando] = useState(false)
  const [puntajes, setPun] = useState({})
  const [avisoReintentar, setAvisoReintentar] = useState(false)
  const grabacionDisponible = useMemo(haySoporteGrabacionPronunciacion, [])
  const grabadorRef = useRef(null)
  const timeoutAutoParoRef = useRef(null)
  const palabra = palabras[idx]

  function marcarPracticada() {
    setPun(p => ({ ...p, [palabra.id]: 'auto' }))
  }

  function limpiarTimeoutAutoParo() {
    if (timeoutAutoParoRef.current) { clearTimeout(timeoutAutoParoRef.current); timeoutAutoParoRef.current = null }
  }

  async function iniciarGrabacion() {
    if (!grabacionDisponible) { marcarPracticada(); return }
    setAvisoReintentar(false)
    const grabador = new GrabadorPronunciacion()
    try {
      await grabador.iniciar()
    } catch (err) {
      // El estudiante nego el permiso del microfono, u otro error del
      // navegador -- no hay forma de grabar, asi que caemos a la
      // alternativa auto-reportada en vez de dejarlo sin poder avanzar.
      marcarPracticada()
      return
    }
    grabadorRef.current = grabador
    setEsc(true)
    timeoutAutoParoRef.current = setTimeout(detenerYEvaluar, DURACION_MAXIMA_MS)
  }

  async function detenerYEvaluar() {
    limpiarTimeoutAutoParo()
    const grabador = grabadorRef.current
    if (!grabador) return
    const audioWav = grabador.detener()
    grabadorRef.current = null
    setEsc(false)

    if (!audioWav) { setAvisoReintentar(true); return }

    setEvaluando(true)
    try {
      const formData = new FormData()
      formData.append('audio', audioWav, 'grabacion.wav')
      formData.append('referenceText', palabra.texto)
      const { data } = await axios.post(`${API}/pronunciacion/evaluar`, formData)
      if (data.ok && data.reconocido) {
        setPun(p => ({ ...p, [palabra.id]: data.puntajePronunciacion }))
      } else if (data.ok && !data.reconocido) {
        setAvisoReintentar(true)
      } else {
        // El servicio no esta configurado o fallo -- no bloqueamos al
        // estudiante, lo dejamos marcar que ya practico.
        marcarPracticada()
      }
    } catch (err) {
      marcarPracticada()
    } finally {
      setEvaluando(false)
    }
  }

  function cancelarGrabacion() {
    limpiarTimeoutAutoParo()
    if (grabadorRef.current) { grabadorRef.current.cancelar(); grabadorRef.current = null }
    setEsc(false)
  }

  useEffect(() => {
    // Si el estudiante cambia de palabra (o sale de la actividad) a mitad
    // de una grabacion, la cancelamos para no dejar el microfono abierto.
    return () => cancelarGrabacion()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx])

  function siguiente() {
    setAvisoReintentar(false)
    if (idx < palabras.length-1) setIdx(i => i+1)
    else onCompletar({ xp_ganado: actividad.xp })
  }

  if (!palabra) return null
  const puntaje = puntajes[palabra.id]

  return (
    <div>
      <div style={{ background:'linear-gradient(135deg,#E8EFFE,#fff)', border:'1.5px solid #E2E6F0', borderRadius:16, padding:28, textAlign:'center', marginBottom:20 }}>
        <div style={{ fontSize:12, color:'#6B7280', marginBottom:8 }}>{idx+1} / {palabras.length}</div>
        <div style={{ fontSize:20, fontWeight:700, color:'#1B3A8C', marginBottom:4 }}>{palabra.texto}</div>
        {palabra.traduccion && <div style={{ fontSize:13, color:'#9CA3AF', fontStyle:'italic', marginBottom:8 }}>{palabra.traduccion}</div>}
        <div style={{ fontSize:13, color:'#8A6D1D', fontStyle:'italic', marginBottom:12 }}>{palabra.fonetica}</div>
        {palabra.tip && (
          <div style={{ background:'#FBF3D9', borderRadius:8, padding:'8px 14px', fontSize:13, color:'#374151', textAlign:'left', marginBottom:16 }}>
            💡 {palabra.tip}
          </div>
        )}
        {puntaje === 'auto' && (
          <div style={{ fontSize:15, fontWeight:700, color:'#1B3A8C', marginBottom:12 }}>
            ✓ Marcada como practicada
          </div>
        )}
        {typeof puntaje === 'number' && (
          <div style={{ fontSize:18, fontWeight:700, color: puntaje>=(palabra.puntuacion_minima||60) ? '#22C55E' : '#F59E0B', marginBottom:12 }}>
            {puntaje}% — {puntaje>=(palabra.puntuacion_minima||60) ? 'Great job!' : 'Try again'}
          </div>
        )}
        {avisoReintentar && (
          <div style={{ fontSize:13, color:'#F59E0B', marginBottom:12 }}>
            No logramos escucharte bien. Intenta de nuevo, más cerca del micrófono.
          </div>
        )}
        {grabacionDisponible ? (
          <div style={{ display:'flex', justifyContent:'center', gap:10 }}>
            <button onClick={() => hablar(palabra.texto)} disabled={escuchando || evaluando}
              style={{ background:'#E8EFFE', color:'#1B3A8C', border:'1.5px solid #1B3A8C', borderRadius:10, padding:'9px 16px', fontFamily:'inherit', fontSize:13, fontWeight:600, cursor:'pointer' }}>
              🔊 Escuchar
            </button>
            {escuchando ? (
              <button onClick={detenerYEvaluar}
                style={{ background:'#EF4444', color:'#fff', border:'none', borderRadius:10, padding:'9px 16px', fontFamily:'inherit', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                ⏹ Detener y evaluar
              </button>
            ) : (
              <button onClick={iniciarGrabacion} disabled={evaluando}
                style={{ background: evaluando ? '#9CA3AF' : '#1B3A8C', color:'#fff', border:'none', borderRadius:10, padding:'9px 16px', fontFamily:'inherit', fontSize:13, fontWeight:600, cursor: evaluando ? 'default' : 'pointer' }}>
                {evaluando ? 'Evaluando...' : '🎙 Grabar mi voz'}
              </button>
            )}
          </div>
        ) : (
          <div>
            <div style={{ display:'flex', justifyContent:'center', gap:10, marginBottom:8 }}>
              <button onClick={() => hablar(palabra.texto)}
                style={{ background:'#E8EFFE', color:'#1B3A8C', border:'1.5px solid #1B3A8C', borderRadius:10, padding:'9px 16px', fontFamily:'inherit', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                🔊 Escuchar
              </button>
              <button onClick={marcarPracticada}
                style={{ background:'#1B3A8C', color:'#fff', border:'none', borderRadius:10, padding:'9px 16px', fontFamily:'inherit', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                ✓ Ya la practiqué en voz alta
              </button>
            </div>
            <p style={{ fontSize:11, color:'#9CA3AF' }}>
              Tu navegador no soporta grabar tu pronunciación. Escucha la palabra, repítela en voz alta, y marca que ya la practicaste para continuar.
            </p>
          </div>
        )}
      </div>
      <div style={{ display:'flex', justifyContent:'flex-end', paddingTop:16, borderTop:'1px solid #E2E6F0' }}>
        <button onClick={siguiente} disabled={escuchando || evaluando}
          style={{ background: (escuchando || evaluando) ? '#E2E6F0' : '#D4AF37', color: (escuchando || evaluando) ? '#6B7280' : '#0D1F52', border:'none', borderRadius:10, padding:'9px 20px', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor: (escuchando || evaluando) ? 'default' : 'pointer' }}>
          {idx < palabras.length-1 ? 'Siguiente →' : 'Finalizar actividad'}
        </button>
      </div>
    </div>
  )
}

// ── ACT: SPEAKING (respuesta oral libre, modo Legend) ─────
function ActividadSpeaking({ actividad, onCompletar }) {
  const c = actividad.contenido || {}
  const [escuchando, setEsc] = useState(false)
  const [transcripcion, setTranscripcion] = useState('')
  const [enviado, setEnviado] = useState(false)
  const palabras = transcripcion.trim().split(/\s+/).filter(Boolean)
  const minimoPalabras = c.palabras_minimas || 12
  const suficiente = palabras.length >= minimoPalabras
  const validacion = validarContenidoIngles(transcripcion)
  const puedeEnviar = suficiente && validacion.valido
  const factorEsfuerzo = calcularFactorEsfuerzo(transcripcion, c.palabras_clave, minimoPalabras)
  const xpGanado = Math.round((actividad.xp || 0) * factorEsfuerzo)
  const speechDisponible = useMemo(hayReconocimientoDeVoz, [])

  function grabar() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SR) return
    const rec = new SR()
    rec.lang = 'en-US'; rec.interimResults = false; rec.continuous = false
    setEsc(true)
    rec.onresult = e => {
      const dicho = e.results[0][0].transcript
      setTranscripcion(t => (t ? t + ' ' : '') + dicho)
      setEsc(false)
    }
    rec.onerror = () => setEsc(false)
    rec.onend = () => setEsc(false)
    rec.start()
  }

  return (
    <div>
      <div style={{ background:'#F4F6FA', borderRadius:12, padding:16, marginBottom:16 }}>
        <p style={{ fontSize:14, fontWeight:600, color:'#1B3A8C', marginBottom:4 }}>🎙 {c.prompt}</p>
        {c.prompt_traduccion && <p style={{ fontSize:12, color:'#9CA3AF', fontStyle:'italic' }}>{c.prompt_traduccion}</p>}
        {c.tiempo_sugerido_seg && <p style={{ fontSize:11, color:'#8A6D1D', marginTop:6 }}>⏱ Habla durante al menos {c.tiempo_sugerido_seg} segundos.</p>}
      </div>
      {c.palabras_clave && (
        <div style={{ marginBottom:14 }}>
          <p style={{ fontSize:12, fontWeight:600, color:'#6B7280', marginBottom:6 }}>SUGGESTED WORDS:</p>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {c.palabras_clave.map((p,i) => (
              <button key={i} onClick={() => hablar(p)}
                style={{ background:'#E8EFFE', border:'1px solid #1B3A8C', borderRadius:999, padding:'3px 10px', fontSize:12, color:'#1B3A8C', cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
                {p} 🔊
                {c.palabras_clave_traduccion && <span style={{ color:'#9CA3AF', fontSize:11 }}>({c.palabras_clave_traduccion[i]})</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      {!enviado ? (
        <>
          <div style={{ background:'linear-gradient(135deg,#E8EFFE,#fff)', border:'1.5px solid #E2E6F0', borderRadius:16, padding:24, textAlign:'center', marginBottom:16 }}>
            {speechDisponible ? (
              <button onClick={grabar} disabled={escuchando}
                style={{ background: escuchando ? '#EF4444' : '#1B3A8C', color:'#fff', border:'none', borderRadius:10, padding:'12px 24px', fontFamily:'inherit', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                {escuchando ? '⏹ Escuchando...' : '🎙 Grabar mi respuesta'}
              </button>
            ) : (
              <div>
                <p style={{ fontSize:12, color:'#8A6D1D', marginBottom:10 }}>
                  Tu navegador no soporta grabar tu voz (usa Chrome o Edge para eso). Di tu respuesta en voz alta para practicar, y escríbela aquí:
                </p>
                <textarea value={transcripcion} onChange={e => setTranscripcion(e.target.value)}
                  placeholder="Write in English what you said..."
                  style={{ width:'100%', minHeight:100, padding:'12px 14px', border:'1.5px solid #E2E6F0', borderRadius:10, fontFamily:'inherit', fontSize:14, outline:'none', resize:'vertical', lineHeight:1.6 }}
                />
              </div>
            )}
            {transcripcion && speechDisponible && (
              <div style={{ marginTop:16, textAlign:'left', background:'#fff', border:'1px solid #E2E6F0', borderRadius:10, padding:12 }}>
                <p style={{ fontSize:12, fontWeight:600, color:'#6B7280', marginBottom:4 }}>LO QUE DIJISTE:</p>
                <p style={{ fontSize:14, lineHeight:1.6 }}>{transcripcion}</p>
                <button onClick={() => setTranscripcion('')}
                  style={{ marginTop:8, background:'none', border:'none', color:'#EF4444', fontSize:12, cursor:'pointer', textDecoration:'underline' }}>
                  Borrar y grabar de nuevo
                </button>
              </div>
            )}
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:8 }}>
            <span style={{ fontSize:12, color: suficiente ? '#22C55E' : '#6B7280' }}>
              {palabras.length} palabras habladas {suficiente ? '✓ Listo' : '· sigue hablando un poco mas'}
            </span>
            <button disabled={!puedeEnviar} onClick={() => setEnviado(true)}
              style={{ background: puedeEnviar ? '#1B3A8C' : '#E2E6F0', color: puedeEnviar ? '#fff' : '#6B7280', border:'none', borderRadius:10, padding:'9px 20px', fontFamily:'inherit', fontSize:14, fontWeight:600, cursor: puedeEnviar ? 'pointer' : 'not-allowed' }}>
              Enviar respuesta
            </button>
          </div>
          {suficiente && !validacion.valido && (
            <p style={{ fontSize:12, color:'#EF4444', marginTop:6 }}>{validacion.motivo}</p>
          )}
        </>
      ) : (
        <div>
          <div style={{ background:'#F0FDF4', border:'1px solid #22C55E', borderRadius:10, padding:16, marginBottom:16 }}>
            <p style={{ fontSize:13, fontWeight:600, color:'#166534', marginBottom:8 }}>✅ Tu respuesta hablada:</p>
            <p style={{ fontSize:14, lineHeight:1.7 }}>{transcripcion}</p>
          </div>
          {c.ejemplo_respuesta && (
            <details>
              <summary style={{ cursor:'pointer', fontSize:13, fontWeight:600, color:'#1B3A8C', padding:'6px 0' }}>Ver ejemplo de respuesta</summary>
              <div style={{ marginTop:8, background:'#E8EFFE', borderRadius:8, padding:12 }}>
                <p style={{ fontSize:13, lineHeight:1.7 }}>{c.ejemplo_respuesta}</p>
                {c.ejemplo_traduccion && <p style={{ fontSize:12, color:'#9CA3AF', fontStyle:'italic', marginTop:4 }}>{c.ejemplo_traduccion}</p>}
                <button onClick={() => hablar(c.ejemplo_respuesta)} style={{ marginTop:8, background:'#1B3A8C', border:'none', borderRadius:8, padding:'6px 12px', color:'#fff', fontSize:12, cursor:'pointer' }}>🔊 Escuchar ejemplo</button>
              </div>
            </details>
          )}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:16, paddingTop:16, borderTop:'1px solid #E2E6F0' }}>
            <span style={{ fontSize:14, fontWeight:700, color:'#22C55E' }}>✅ +{xpGanado} XP</span>
            <button onClick={() => onCompletar({ xp_ganado: xpGanado })}
              style={{ background:'#D4AF37', color:'#0D1F52', border:'none', borderRadius:10, padding:'9px 20px', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor:'pointer' }}>
              Continuar →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── ACT 8: DICTADO (escucha y escribe) ────────────────────
function ActividadDictado({ actividad, onCompletar }) {
  const frases = actividad.contenido?.frases || []
  const [respuestas, setResp] = useState({})
  const [validado, setVal] = useState(false)
  const [correctas, setCor] = useState(0)

  // Comparacion tolerante: ignora mayusculas, signos de puntuacion y
  // espacios extra, para no penalizar diferencias que no importan al oido.
  function normalizar(txt) {
    return (txt || '').trim().toLowerCase().replace(/[.,!?¿¡]/g, '').replace(/\s+/g, ' ')
  }

  function validar() {
    let ok = 0
    frases.forEach(f => { if (normalizar(respuestas[f.id]) === normalizar(f.texto)) ok++ })
    setCor(ok); setVal(true)
  }

  const xpGanado = Math.round((correctas / Math.max(frases.length, 1)) * actividad.xp)
  const todasRespondidas = frases.every(f => (respuestas[f.id] || '').trim().length > 0)

  return (
    <div>
      <div style={{ display:'flex', flexDirection:'column', gap:20, marginBottom:24 }}>
        {frases.map((f, i) => {
          const ok = validado && normalizar(respuestas[f.id]) === normalizar(f.texto)
          return (
            <div key={f.id}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                <span style={{ fontSize:12, fontWeight:700, color:'#6B7280' }}>{i+1}.</span>
                <button onClick={() => hablar(f.texto)}
                  style={{ background:'#1B3A8C', border:'none', borderRadius:8, width:32, height:32, cursor:'pointer', color:'#fff', fontSize:14, flexShrink:0 }}>
                  🔊
                </button>
                <span style={{ fontSize:12, color:'#9CA3AF' }}>Escucha las veces que necesites y escribe lo que oigas</span>
              </div>
              <input type="text" value={respuestas[f.id] || ''} disabled={validado}
                onChange={e => setResp(r => ({ ...r, [f.id]: e.target.value }))}
                placeholder="Escribe la frase aqui..."
                style={{ width:'100%', padding:'10px 14px', border:`1.5px solid ${validado ? (ok ? '#22C55E' : '#EF4444') : '#E2E6F0'}`, borderRadius:10, fontFamily:'inherit', fontSize:14, outline:'none', boxSizing:'border-box', background: validado ? (ok ? '#F0FDF4' : '#FEF2F2') : '#fff' }}
              />
              {validado && !ok && <div style={{ marginTop:6, fontSize:12, color:'#991B1B' }}>Correcto: "{f.texto}"</div>}
              {f.traduccion && <div style={{ marginTop:4, fontSize:11, color:'#9CA3AF', fontStyle:'italic' }}>{f.traduccion}</div>}
            </div>
          )
        })}
      </div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingTop:16, borderTop:'1px solid #E2E6F0' }}>
        {!validado
          ? <button disabled={!todasRespondidas} onClick={validar}
              style={{ background: todasRespondidas ? '#1B3A8C' : '#E2E6F0', color: todasRespondidas ? '#fff' : '#6B7280', border:'none', borderRadius:10, padding:'9px 20px', fontFamily:'inherit', fontSize:14, fontWeight:600, cursor: todasRespondidas ? 'pointer' : 'not-allowed' }}>
              Comprobar
            </button>
          : <>
              <span style={{ fontSize:14, fontWeight:700, color:'#22C55E' }}>✅ {correctas}/{frases.length} correctas · +{xpGanado} XP</span>
              <button onClick={() => onCompletar({ xp_ganado: xpGanado })}
                style={{ background:'#D4AF37', color:'#0D1F52', border:'none', borderRadius:10, padding:'9px 20px', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor:'pointer' }}>
                Siguiente →
              </button>
            </>
        }
      </div>
    </div>
  )
}

// ── ACT 9: RONDA RAPIDA (vocabulario contrarreloj) ────────
function generarOpcionesRonda(palabraActual, todas) {
  const distractores = todas.filter(p => p.id !== palabraActual.id).map(p => p.traduccion)
  const elegidos = [...distractores].sort(() => Math.random() - 0.5).slice(0, 3)
  return [...elegidos, palabraActual.traduccion].sort(() => Math.random() - 0.5)
}

function ActividadRondaRapida({ actividad, onCompletar }) {
  const palabras = actividad.contenido?.palabras || []
  const duracionTotal = actividad.contenido?.duracion_segundos || 30
  const [orden] = useState(() => [...palabras].sort(() => Math.random() - 0.5))
  const [idx, setIdx] = useState(0)
  const [segundos, setSegundos] = useState(duracionTotal)
  const [correctas, setCorrectas] = useState(0)
  const [intentadas, setIntentadas] = useState(0)
  const [fase, setFase] = useState('lista') // lista | jugando | terminado
  const [feedback, setFeedback] = useState(null) // 'ok' | 'mal' | null
  const [seleccion, setSeleccion] = useState(null)
  const intervalRef = useRef(null)
  const palabraActual = orden[idx]
  const opciones = useMemo(() => palabraActual ? generarOpcionesRonda(palabraActual, palabras) : [], [palabraActual, palabras])

  useEffect(() => () => clearInterval(intervalRef.current), [])

  function empezar() {
    setFase('jugando')
    intervalRef.current = setInterval(() => {
      setSegundos(s => {
        if (s <= 1) { clearInterval(intervalRef.current); setFase('terminado'); return 0 }
        return s - 1
      })
    }, 1000)
  }

  function responder(opcion) {
    if (feedback) return
    setSeleccion(opcion)
    const ok = opcion === palabraActual.traduccion
    setFeedback(ok ? 'ok' : 'mal')
    setIntentadas(n => n + 1)
    if (ok) setCorrectas(n => n + 1)
    setTimeout(() => {
      setFeedback(null); setSeleccion(null)
      if (idx + 1 < orden.length) {
        setIdx(i => i + 1)
      } else {
        clearInterval(intervalRef.current)
        setFase('terminado')
      }
    }, 500)
  }

  const xpGanado = Math.round((correctas / Math.max(palabras.length, 1)) * actividad.xp)

  if (fase === 'lista') return (
    <div style={{ textAlign:'center', padding:'30px 0' }}>
      <div style={{ fontSize:40, marginBottom:12 }}>⚡</div>
      <p style={{ fontSize:14, color:'#6B7280', marginBottom:20 }}>
        Tienes {duracionTotal} segundos para elegir la traduccion correcta de cada palabra. ¡Se rapido!
      </p>
      <button onClick={empezar}
        style={{ background:'#D4AF37', color:'#0D1F52', border:'none', borderRadius:12, padding:'12px 32px', fontFamily:'inherit', fontSize:15, fontWeight:700, cursor:'pointer' }}>
        Empezar ronda
      </button>
    </div>
  )

  if (fase === 'terminado') return (
    <div style={{ textAlign:'center', padding:'20px 0' }}>
      <div style={{ fontSize:40, marginBottom:12 }}>{correctas === palabras.length ? '🏆' : '⏱'}</div>
      <p style={{ fontSize:18, fontWeight:800, color:'#1B3A8C', marginBottom:6 }}>{correctas} / {palabras.length} correctas</p>
      <p style={{ fontSize:13, color:'#6B7280', marginBottom:20 }}>{intentadas} palabras intentadas en {duracionTotal}s</p>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:16 }}>
        <span style={{ fontSize:15, fontWeight:700, color:'#22C55E' }}>✅ +{xpGanado} XP</span>
        <button onClick={() => onCompletar({ xp_ganado: xpGanado })}
          style={{ background:'#D4AF37', color:'#0D1F52', border:'none', borderRadius:10, padding:'9px 24px', fontFamily:'inherit', fontSize:14, fontWeight:700, cursor:'pointer' }}>
          Siguiente →
        </button>
      </div>
    </div>
  )

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <span style={{ fontSize:12, color:'#6B7280' }}>{idx+1}/{orden.length}</span>
        <span style={{ fontSize:18, fontWeight:800, color: segundos <= 5 ? '#EF4444' : '#1B3A8C' }}>⏱ {segundos}s</span>
      </div>
      <div style={{ textAlign:'center', marginBottom:24 }}>
        <div style={{ fontSize:26, fontWeight:800, color:'#1A1A2E' }}>{palabraActual?.texto}</div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        {opciones.map((op, i) => {
          const esCorrecta = op === palabraActual.traduccion
          let bg='#fff', border='#E2E6F0', color='#1A1A2E'
          if (feedback && esCorrecta) { bg='#F0FDF4'; border='#22C55E'; color='#166534' }
          if (feedback === 'mal' && op === seleccion) { bg='#FEF2F2'; border='#EF4444'; color='#991B1B' }
          return (
            <button key={i} onClick={() => responder(op)} disabled={!!feedback}
              style={{ padding:'14px 12px', border:`2px solid ${border}`, borderRadius:10, background:bg, color, fontFamily:'inherit', fontSize:14, fontWeight:600, cursor: feedback ? 'default' : 'pointer' }}>
              {op}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── PANTALLA VIDEO DOCENTE ────────────────────────────────
function PantallaVideo({ leccion, onContinuar }) {
  const [visto, setVisto] = useState(false)
  const tieneVideo = leccion?.video_url

  return (
    <div style={{ maxWidth:760, margin:'0 auto', animation:'fadeIn .3s ease both' }}>
      <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:32 }}>
        <div style={{ textAlign:'center', marginBottom:24 }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🎬</div>
          <h2 style={{ fontSize:22, fontWeight:800, color:'#1B3A8C', marginBottom:8 }}>
            Teacher Review
          </h2>
          <p style={{ fontSize:14, color:'#6B7280' }}>
            Watch your teacher explain the key concepts of this lesson before continuing.
          </p>
        </div>

        {tieneVideo ? (
          <div style={{ marginBottom:24 }}>
            <div style={{ background:'#000', borderRadius:12, overflow:'hidden', aspectRatio:'16/9', marginBottom:12 }}>
              <iframe
                src={leccion.video_url}
                title={leccion.video_titulo || 'Teacher Review'}
                width="100%" height="100%"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                onEnded={() => setVisto(true)}
                style={{ display:'block' }}
              />
            </div>
            {leccion.video_titulo && (
              <p style={{ fontSize:13, fontWeight:600, color:'#374151' }}>📹 {leccion.video_titulo}</p>
            )}
            {leccion.video_duracion && (
              <p style={{ fontSize:12, color:'#9CA3AF' }}>⏱ Duration: {leccion.video_duracion}</p>
            )}
          </div>
        ) : (
          <div style={{ background:'linear-gradient(135deg,#0D1F52,#1B3A8C)', borderRadius:12, padding:32, textAlign:'center', color:'#fff', marginBottom:24 }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🚧</div>
            <p style={{ fontSize:15, fontWeight:600, marginBottom:4 }}>Video coming soon</p>
            <p style={{ fontSize:13, opacity:.7 }}>Your teacher is preparing the review video for this lesson.</p>
          </div>
        )}

        <label style={{ display:'flex', alignItems:'center', gap:10, fontSize:14, cursor:'pointer', marginBottom:20, padding:'12px 16px', background:'#F8FAFF', borderRadius:10, border:'1px solid #E2E6F0' }}>
          <input type="checkbox" checked={visto} onChange={e => setVisto(e.target.checked)}
            style={{ width:18, height:18, cursor:'pointer' }} />
          <span>I have watched the teacher review video</span>
        </label>

        <button disabled={!visto} onClick={onContinuar}
          style={{ width:'100%', padding:'14px', background: visto ? 'linear-gradient(135deg,#D4AF37,#B8912C)' : '#E2E6F0', color: visto ? '#0D1F52' : '#9CA3AF', border:'none', borderRadius:12, fontFamily:'inherit', fontSize:16, fontWeight:700, cursor: visto ? 'pointer' : 'not-allowed' }}>
          {visto ? 'Continue to next lesson →' : 'Watch the video first'}
        </button>
      </div>
    </div>
  )
}

// ── EXAMEN DE UNIDAD ──────────────────────────────────────
function ExamenUnidad({ unidadId, unidadNombre, usuario, onAprobado }) {
  const [preguntas, setPreguntas]   = useState([])
  const [respuestas, setRespuestas] = useState({})
  const [validado, setValidado]     = useState(false)
  const [resultado, setResultado]   = useState(null)
  const [cargando, setCargando]     = useState(true)
  const [intento, setIntento]       = useState(1)

  useEffect(() => { cargarPreguntas() }, [unidadId])

  async function cargarPreguntas() {
    setCargando(true)
    try {
      const res = await axios.get(`${API}/curriculo/unidades/${unidadId}/examen`)
      setPreguntas(res.data.datos || [])
    } catch (err) { console.error(err) }
    finally { setCargando(false) }
  }

  async function validar() {
    // La calificacion ya no se calcula aqui — el navegador manda las
    // respuestas elegidas y el servidor es quien decide cuantas estan
    // bien, el porcentaje y si aprobo, comparando contra la respuesta
    // correcta real (que nunca se le manda al navegador antes de esto).
    try {
      const res = await axios.post(`${API}/curriculo/unidades/${unidadId}/examen/guardar`, {
        usuario_id: usuario.id,
        respuestas
      })
      const { correctas, total, porcentaje, aprobado, detalle } = res.data
      setResultado({ correctas, total, porcentaje, aprobado, detalle })
      setValidado(true)
    } catch (err) {
      console.error(err)
      alert('No pudimos calificar el examen. Intenta de nuevo.')
    }
  }

  function reintentar() {
    setRespuestas({})
    setValidado(false)
    setResultado(null)
    setIntento(i => i+1)
  }

  if (cargando) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:60, color:'#6B7280' }}>
      <div style={{ width:32, height:32, border:'3px solid #E2E6F0', borderTopColor:'#1B3A8C', borderRadius:'50%', animation:'spinner .8s linear infinite' }} />
    </div>
  )

  if (preguntas.length === 0) return (
    <div style={{ maxWidth:760, margin:'0 auto' }}>
      <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:32, textAlign:'center' }}>
        <div style={{ fontSize:48, marginBottom:12 }}>🚧</div>
        <h2 style={{ fontSize:20, fontWeight:800, color:'#1B3A8C', marginBottom:8 }}>Exam coming soon</h2>
        <p style={{ fontSize:14, color:'#6B7280', marginBottom:24 }}>The unit exam is being prepared. You can continue to the next lesson.</p>
        <button onClick={onAprobado}
          style={{ background:'#1B3A8C', color:'#fff', border:'none', borderRadius:12, padding:'12px 28px', fontFamily:'inherit', fontSize:15, fontWeight:700, cursor:'pointer' }}>
          Continue →
        </button>
      </div>
    </div>
  )

  return (
    <div style={{ maxWidth:760, margin:'0 auto', animation:'fadeIn .3s ease both' }}>
      <div style={{ background:'linear-gradient(135deg,#0D1F52,#1B3A8C)', borderRadius:14, padding:24, color:'#fff', marginBottom:24, display:'flex', alignItems:'center', gap:16 }}>
        <div style={{ fontSize:40 }}>📝</div>
        <div>
          <div style={{ fontSize:12, opacity:.7, marginBottom:2 }}>Unit Exam</div>
          <div style={{ fontSize:20, fontWeight:800 }}>{unidadNombre}</div>
          <div style={{ fontSize:13, opacity:.8, marginTop:4 }}>
            {preguntas.length} questions · Minimum 80% to pass · Unlimited attempts
          </div>
        </div>
        {intento > 1 && (
          <div style={{ marginLeft:'auto', background:'rgba(255,255,255,0.15)', borderRadius:10, padding:'6px 14px', fontSize:13, fontWeight:600 }}>
            Attempt #{intento}
          </div>
        )}
      </div>

      {!validado ? (
        <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:32 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:28, marginBottom:28 }}>
            {preguntas.map((p, i) => (
              <div key={p.id}>
                <p style={{ fontSize:15, fontWeight:600, color:'#1A1A2E', marginBottom:12 }}>
                  <span style={{ color:'#6B7280', marginRight:8 }}>{i+1}.</span>{p.pregunta}
                </p>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {[
                    { id:'a', texto: p.opcion_a },
                    { id:'b', texto: p.opcion_b },
                    { id:'c', texto: p.opcion_c },
                    { id:'d', texto: p.opcion_d },
                  ].map(op => (
                    <button key={op.id} onClick={() => setRespuestas(r => ({...r, [p.id]: op.id}))}
                      style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 16px',
                        border:`2px solid ${respuestas[p.id]===op.id ? '#1B3A8C' : '#E2E6F0'}`,
                        borderRadius:10, background: respuestas[p.id]===op.id ? '#E8EFFE' : '#fff',
                        fontFamily:'inherit', fontSize:14, cursor:'pointer', textAlign:'left', width:'100%' }}>
                      <span style={{ width:28, height:28, borderRadius:'50%',
                        background: respuestas[p.id]===op.id ? '#1B3A8C' : '#F4F6FA',
                        color: respuestas[p.id]===op.id ? '#fff' : '#6B7280',
                        display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800, flexShrink:0 }}>
                        {op.id.toUpperCase()}
                      </span>
                      {op.texto}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button
            disabled={Object.keys(respuestas).length < preguntas.length}
            onClick={validar}
            style={{ width:'100%', padding:'14px', background: Object.keys(respuestas).length < preguntas.length ? '#E2E6F0' : '#1B3A8C', color: Object.keys(respuestas).length < preguntas.length ? '#9CA3AF' : '#fff', border:'none', borderRadius:12, fontFamily:'inherit', fontSize:16, fontWeight:700, cursor: Object.keys(respuestas).length < preguntas.length ? 'not-allowed' : 'pointer' }}>
            Submit exam ({Object.keys(respuestas).length}/{preguntas.length} answered)
          </button>
        </div>
      ) : (
        <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:32 }}>
          <div style={{ textAlign:'center', marginBottom:28 }}>
            <div style={{ fontSize:64, marginBottom:12 }}>{resultado.aprobado ? '🎉' : '😔'}</div>
            <h2 style={{ fontSize:28, fontWeight:900, color: resultado.aprobado ? '#22C55E' : '#EF4444', marginBottom:8 }}>
              {resultado.aprobado ? 'You passed!' : 'Not yet...'}
            </h2>
            <div style={{ fontSize:48, fontWeight:900, color: resultado.aprobado ? '#22C55E' : '#EF4444', marginBottom:4 }}>
              {resultado.porcentaje}%
            </div>
            <p style={{ fontSize:15, color:'#6B7280', marginBottom:4 }}>
              {resultado.correctas} out of {resultado.total} correct
            </p>
            <p style={{ fontSize:13, color: resultado.aprobado ? '#22C55E' : '#EF4444', fontWeight:600 }}>
              {resultado.aprobado ? '✅ Minimum 80% reached' : '❌ You need at least 80% to pass'}
            </p>
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:16, marginBottom:28 }}>
            {preguntas.map((p, i) => {
              // La respuesta correcta y la explicacion ya no vienen en
              // "preguntas" (el GET del examen nunca las manda) — ahora
              // llegan en "resultado.detalle", que el servidor arma al
              // calificar.
              const det = (resultado.detalle || []).find(d => d.id === p.id) || {}
              const resp = det.respuesta_usuario
              const correcto = det.correcta
              return (
                <div key={p.id} style={{ border:`1.5px solid ${correcto ? '#22C55E' : '#EF4444'}`, borderRadius:12, padding:16 }}>
                  <p style={{ fontSize:14, fontWeight:600, marginBottom:8, color:'#1A1A2E' }}>
                    <span style={{ color:'#6B7280', marginRight:8 }}>{i+1}.</span>{p.pregunta}
                  </p>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {[
                      { id:'a', texto: p.opcion_a },
                      { id:'b', texto: p.opcion_b },
                      { id:'c', texto: p.opcion_c },
                      { id:'d', texto: p.opcion_d },
                    ].map(op => {
                      const esCorrecta = op.id === det.respuesta_correcta
                      const esRespuesta = op.id === resp
                      let bg = '#fff', border = '#E2E6F0', color = '#374151'
                      if (esCorrecta) { bg='#F0FDF4'; border='#22C55E'; color='#166534' }
                      if (esRespuesta && !esCorrecta) { bg='#FEF2F2'; border='#EF4444'; color='#991B1B' }
                      return (
                        <div key={op.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', border:`1.5px solid ${border}`, borderRadius:8, background:bg, color, fontSize:13 }}>
                          <span style={{ width:22, height:22, borderRadius:'50%', background: esCorrecta ? '#22C55E' : esRespuesta ? '#EF4444' : '#F4F6FA', color: (esCorrecta || esRespuesta) ? '#fff' : '#6B7280', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:800, flexShrink:0 }}>
                            {op.id.toUpperCase()}
                          </span>
                          {op.texto}
                          {esCorrecta && <span style={{ marginLeft:'auto', fontSize:14 }}>✓</span>}
                          {esRespuesta && !esCorrecta && <span style={{ marginLeft:'auto', fontSize:14 }}>✗</span>}
                        </div>
                      )
                    })}
                  </div>
                  {det.explicacion && (
                    <div style={{ marginTop:10, padding:'8px 12px', background:'#F8FAFF', borderRadius:8, fontSize:12, color:'#6B7280', borderLeft:'3px solid #1B3A8C' }}>
                      💡 {det.explicacion}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <div style={{ display:'flex', gap:12 }}>
            {!resultado.aprobado && (
              <button onClick={reintentar}
                style={{ flex:1, padding:'14px', background:'#F4F6FA', color:'#374151', border:'1.5px solid #E2E6F0', borderRadius:12, fontFamily:'inherit', fontSize:15, fontWeight:700, cursor:'pointer' }}>
                🔄 Try again
              </button>
            )}
            {resultado.aprobado && (
              <button onClick={onAprobado}
                style={{ flex:1, padding:'14px', background:'linear-gradient(135deg,#22C55E,#16A34A)', color:'#fff', border:'none', borderRadius:12, fontFamily:'inherit', fontSize:15, fontWeight:700, cursor:'pointer' }}>
                🚀 Continue to next unit →
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── MOTOR PRINCIPAL ───────────────────────────────────────
export default function PaginaLeccion({ leccionId, onVolver, usuario, unidadId, unidadNombre, esUltimaLeccion }) {
  const [leccion,     setLeccion]     = useState(null)
  const [actividades, setActividades] = useState([])
  const [actActual,   setActActual]   = useState(0)
  const [fase,        setFase]        = useState('actividades') // actividades | video | examen | completada
  const [xpTotal,     setXpTotal]     = useState(0)
  const [cargando,    setCargando]    = useState(true)

  useEffect(() => { precargarMapaAudios() }, [])

  useEffect(() => {
    if (!leccionId) return
    setCargando(true)
    Promise.all([
      axios.get(`${API}/curriculo/lecciones/${leccionId}`),
      axios.get(`${API}/curriculo/lecciones/${leccionId}/actividades`)
    ]).then(([l,a]) => {
      setLeccion(l.data.datos)
      setActividades(seleccionarActividadesDeSesion(a.data.datos || []))
    }).finally(() => setCargando(false))
  }, [leccionId])

  function handleCompletar(datos) {
    const nuevoXp = xpTotal + (datos.xp_ganado||0)
    setXpTotal(nuevoXp)
    if (actActual < actividades.length-1) {
      setActActual(i => i+1)
    } else {
      if (usuario?.id) {
        axios.post(`${API}/progreso/completar-leccion`, {
          usuario_id: usuario.id,
          leccion_id: leccionId,
          xp_ganado: nuevoXp
        }).catch(console.error)
      }
      setFase('video')
    }
  }

  function handleVideoVisto() {
    if (esUltimaLeccion && unidadId) {
      setFase('examen')
    } else {
      setFase('completada')
    }
  }

  if (cargando) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:60, gap:16, color:'#6B7280' }}>
      <div style={{ width:32, height:32, border:'3px solid #E2E6F0', borderTopColor:'#1B3A8C', borderRadius:'50%', animation:'spinner .8s linear infinite' }} />
      Cargando leccion...
    </div>
  )

  if (fase === 'video') return (
    <PantallaVideo leccion={leccion} onContinuar={handleVideoVisto} />
  )

  if (fase === 'examen') return (
    <ExamenUnidad
      unidadId={unidadId}
      unidadNombre={unidadNombre}
      usuario={usuario}
      onAprobado={() => setFase('completada')}
    />
  )

  if (fase === 'completada') return (
    <div style={{ textAlign:'center', padding:60, display:'flex', flexDirection:'column', alignItems:'center', gap:16 }}>
      <div style={{ fontSize:64 }}>🎉</div>
      <h2 style={{ fontSize:28, fontWeight:800, color:'#1B3A8C' }}>
        {esUltimaLeccion ? 'Unit completed! 🏆' : 'Lesson completed!'}
      </h2>
      <p style={{ fontSize:16, color:'#6B7280' }}>{leccion?.titulo}</p>
      <div style={{ background:'#E8EFFE', borderRadius:16, padding:'16px 32px', fontSize:24, fontWeight:800, color:'#1B3A8C' }}>
        ⚡ +{xpTotal} XP {esUltimaLeccion && '+ 200 XP bonus!'}
      </div>
      <button onClick={onVolver}
        style={{ background:'#D4AF37', color:'#0D1F52', border:'none', borderRadius:12, padding:'12px 28px', fontFamily:'inherit', fontSize:16, fontWeight:700, cursor:'pointer', marginTop:8 }}>
        Back to curriculum
      </button>
    </div>
  )

  const actividad = actividades[actActual]
  if (!actividad) return null

  const TIPOS = {
    video:            ActividadVideo,
    explicacion:      ActividadExplicacion,
    match:            ActividadMatch,
    opcion_multiple:  ActividadOpcionMultiple,
    produccion_libre: ActividadProduccionLibre,
    completar_frases: ActividadCompletarFrases,
    completar:        ActividadCompletarFrases,
    pronunciacion:    ActividadPronunciacion,
    dictado:          ActividadDictado,
    ronda_rapida:     ActividadRondaRapida,
    speaking:         ActividadSpeaking
  }
  const Componente = TIPOS[actividad.tipo] || ActividadExplicacion
  const pct = Math.round((actActual/actividades.length)*100)

  return (
    <div style={{ maxWidth:760, margin:'0 auto', animation:'fadeIn .3s ease both' }}>
      <div style={{ display:'flex', alignItems:'center', gap:16, padding:'14px 20px', background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, marginBottom:24 }}>
        <button onClick={onVolver} style={{ background:'none', border:'none', cursor:'pointer', fontSize:13, color:'#6B7280', fontFamily:'inherit' }}>← Back</button>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:11, fontWeight:800, color:'#D4AF37' }}>{unidadNombre || 'Lesson'}</div>
          <div style={{ fontSize:14, fontWeight:600 }}>{leccion?.titulo}</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
          <div style={{ width:120, height:6, background:'#E2E6F0', borderRadius:999, overflow:'hidden' }}>
            <div style={{ width:`${pct}%`, height:'100%', background:'#1B3A8C', borderRadius:999, transition:'width .4s ease' }} />
          </div>
          <span style={{ fontSize:12, color:'#6B7280' }}>{actActual+1}/{actividades.length}</span>
        </div>
        <div style={{ background:'#E8EFFE', color:'#1B3A8C', borderRadius:999, padding:'4px 12px', fontSize:13, fontWeight:700 }}>⚡ {leccion?.xp_total} XP</div>
      </div>
      <div style={{ background:'#fff', border:'1px solid #E2E6F0', borderRadius:14, padding:32 }}>
        <h2 style={{ fontSize:20, fontWeight:700, color:'#1B3A8C', marginBottom:6 }}>{actividad.titulo}</h2>
        {actividad.instruccion && <p style={{ fontSize:14, color:'#6B7280', marginBottom:20 }}>{actividad.instruccion}</p>}
        <Componente actividad={actividad} onCompletar={handleCompletar} />
      </div>
    </div>
  )
}
